import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback, } from "react";
import { FaPlayCircle, FaSpinner, FaFilePdf, FaFileWord, FaFileExcel, } from "react-icons/fa";
import { Tooltip } from "@mui/material";
import * as XLSX from "xlsx";
import ReactMarkdown from "react-markdown";
import Tour from "./Tour";
import { CopilotEngine } from "../copilot/copilot_engine";
import CopilotStyles from "./styles/CalybCopliotStyles";
import { convertStylesToCSS } from "./utils/copilot-utils";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import katex from "katex";
import "./styles/custom-katex.css";
import Form from "./Form";
import Tags from "./Tags";
import BlueprintGenerator from "./BluePrintGenerator";
import FeatureTemplates from "./FeatureTemplates";
import { FiPaperclip } from "react-icons/fi";
import Buttons from "./Button";
import Uploader from "./Uploader";
import CalybLogo from "../assets/Calyb.png";
import CalybWhiteLogo from "../assets/calyb-white.png";
import ScoraAvatar from "../assets/ScoraAvatar.png";
const fontImportStyle = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&display=swap');
`;
function CalybCopilot(props) {
    // Initialize CopilotEngine with the client
    const copilotEngine = useRef(new CopilotEngine(props.client)).current;
    const logoPath = props.copilotToggleLogo || CalybLogo;
    const headerLogoPath = props.copilotHeaderLogo || CalybWhiteLogo;
    const avatarPath = ScoraAvatar; // Use imported avatar
    const headerTitle = props.copilotHeaderTitle || "CALYB";
    //console.log(logoPath, props.copilotToggleLogo);
    // Add position state for draggable toggle button
    const [togglePosition, setTogglePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [justDragged, setJustDragged] = useState(false);
    const [job_id, setJobId] = useState(null);
    const [activeLoader, setActiveLoader] = useState(null);
    // State variables
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const textareaRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false); //TODO: Change to false
    const [isMounted, setIsMounted] = useState(false);
    const [isWalkthroughReady, setIsWalkthroughReady] = useState(false);
    const [isWalkthroughActive, setIsWalkthroughActive] = useState(false);
    const [tourSteps, setTourSteps] = useState([]);
    const messagesEndRef = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showStartingUserMessages, setShowStartingUserMessages] = useState(false);
    const [startingUserMessages, setStartingUserMessages] = useState([]);
    const [whitelistedURLs, setWhitelistedURLs] = useState([]);
    const [isResetting, setIsResetting] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredTemplateId, setHoveredTemplateId] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [tags, setTags] = useState([]);
    const [fileData, setFileData] = useState(null);
    const [fileError, setFileError] = useState("");
    const [serverErrorMessage, setServerErrorMessage] = useState("");
    const fileInputRef = useRef(null);
    const [showFilePreview, setShowFilePreview] = useState(false);
    const [fileSummary, setFileSummary] = useState(null);
    const [summaryId, setSummaryId] = useState(null);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [docRemoveAfterAssistantMessages, setDocRemoveAfterAssistantMessages] = useState(null);
    const [clickedButtons, setClickedButtons] = useState({});
    const [selectedFeatureTemplates, setSelectedFeatureTemplates] = useState({});
    const [isChatDisabled, setIsChatDisabled] = useState(true);
    // Track last message width for button alignment
    const [lastMessageWidth, setLastMessageWidth] = useState(0);
    const lastMessageRef = useRef(null);
    // Smart scrolling state and refs
    const messagesContainerRef = useRef(null);
    const messageRefs = useRef(new Map());
    const [lastSeenMessageIndex, setLastSeenMessageIndex] = useState(-1);
    const [messagesBeforeNewBatch, setMessagesBeforeNewBatch] = useState(0);
    const pendingScrollToIndex = useRef(null);
    // Session management
    const [sessionId, setSessionId] = useState(props.sessionId || null);
    // Chat session ID for new session management - generated on browser load
    const [chatSessionId, setChatSessionId] = useState(() => {
        const newSessionId = "session_" + Math.random().toString(36).substr(2, 16);
        console.log("🚀 New chat session ID generated on browser load:", newSessionId);
        return newSessionId;
    });
    const loadingPropmtOptionsDefault = [
        "Let me work on that!",
        "I'll figure this out for you!",
        "Give me a moment...",
        "I'm on it!",
        "Thinking deeply...",
        "Let me see what I can do!",
        "Hang tight, this might take a sec...",
        "Let me check...",
        "One moment while I get that for you!",
    ];
    const [loadingPrompts, setLoadingPrompts] = useState(loadingPropmtOptionsDefault);
    const [featureTemplates, setFeatureTemplates] = useState([]);
    // Add another state for file button hover
    const [isFileButtonHovered, setIsFileButtonHovered] = useState(false);
    const [pendingButtonPrompt, setPendingButtonPrompt] = useState(null);
    // State for file validation options from buttons
    const [fileValidationOptions, setFileValidationOptions] = useState(null);
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 20MB
    // Helper function to show error messages with consistent styling
    const showFileError = (message) => {
        setFileError(message);
        setTimeout(() => setFileError(""), 5000);
    };
    // File validation function for Excel/CSV files
    const validateFileHeaders = async (file, validationOptions) => {
        try {
            // Check accepted types first
            if (validationOptions.acceptedTypes) {
                const acceptedTypesArray = validationOptions.acceptedTypes
                    .split(",")
                    .map((type) => type.trim().toLowerCase());
                const fileExtension = file.name.split(".").pop()?.toLowerCase();
                if (!fileExtension || !acceptedTypesArray.includes(fileExtension)) {
                    return {
                        isValid: false,
                        error: `File type '${fileExtension}' is not allowed. Accepted types: ${validationOptions.acceptedTypes}`,
                    };
                }
            }
            // Only validate headers for Excel and CSV files
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            if (!["xlsx", "csv"].includes(fileExtension || "")) {
                return { isValid: true }; // Non-Excel/CSV files are valid if they pass type check
            }
            // Check if mandatory headers validation is required
            if (!validationOptions.mandatoryHeaders) {
                return { isValid: true }; // No header validation required
            }
            const mandatoryHeadersArray = validationOptions.mandatoryHeaders
                .split(",")
                .map((header) => header.trim());
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const data = e.target?.result;
                        const workbook = XLSX.read(data, { type: "binary" });
                        let tabsToCheck = workbook.SheetNames;
                        // Filter tabs based on tabs_format regex if provided
                        if (validationOptions.tabsFormat) {
                            console.log("=== TAB FORMAT VALIDATION ===");
                            console.log("Original regex pattern from server:", validationOptions.tabsFormat);
                            // Normalize double-escaped backslashes from JSON
                            let normalizedPattern = validationOptions.tabsFormat.replace(/\\\\([sd])/g, "\\$1");
                            console.log("Normalized regex pattern:", normalizedPattern);
                            // Extract flags like (?i) and convert to RegExp flags
                            let flags = "";
                            const flagsMatch = normalizedPattern.match(/^\(\?([gimsuvy]*)\)/);
                            if (flagsMatch) {
                                flags = flagsMatch[1];
                                normalizedPattern = normalizedPattern.replace(/^\(\?[gimsuvy]*\)/, "");
                                console.log("Extracted flags:", flags);
                                console.log("Pattern without flags:", normalizedPattern);
                            }
                            console.log("All available tabs:", workbook.SheetNames);
                            try {
                                const tabsRegex = new RegExp(normalizedPattern, flags);
                                console.log("Compiled regex object:", tabsRegex);
                                // Log each tab comparison
                                const matchResults = workbook.SheetNames.map((sheetName) => {
                                    const matches = tabsRegex.test(sheetName);
                                    console.log(`Tab "${sheetName}" matches pattern: ${matches}`);
                                    return { sheetName, matches };
                                });
                                tabsToCheck = workbook.SheetNames.filter((sheetName) => tabsRegex.test(sheetName));
                                console.log("Matching tabs:", tabsToCheck);
                                console.log("Number of matching tabs:", tabsToCheck.length);
                                if (tabsToCheck.length === 0) {
                                    console.log("❌ VALIDATION FAILED: No tabs matched the pattern");
                                    // Create user-friendly error message with examples
                                    let exampleMessage = "No matching sheet tabs found in the Excel file.";
                                    // Generate examples based on the pattern
                                    if (normalizedPattern.toLowerCase().includes("section")) {
                                        exampleMessage +=
                                            " Expected tabs like: 'Section 1', 'Section 2', etc.";
                                    }
                                    else if (normalizedPattern.match(/\d/)) {
                                        exampleMessage +=
                                            " Expected tabs with numbers (e.g., 'Tab 1', 'Sheet 2').";
                                    }
                                    else {
                                        exampleMessage += ` Expected tab names matching the required format.`;
                                    }
                                    // Add list of available tabs for reference
                                    if (workbook.SheetNames.length > 0) {
                                        exampleMessage += `\n\nAvailable tabs in your file: ${workbook.SheetNames.join(", ")}`;
                                    }
                                    resolve({
                                        isValid: false,
                                        error: exampleMessage,
                                    });
                                    return;
                                }
                                console.log("✅ Pattern matching successful, proceeding to header validation");
                            }
                            catch (regexError) {
                                console.log("❌ VALIDATION FAILED: Invalid regex pattern");
                                console.error("Regex error:", regexError);
                                resolve({
                                    isValid: false,
                                    error: `Invalid regex pattern in tabs_format: ${normalizedPattern}`,
                                });
                                return;
                            }
                        }
                        else {
                            console.log("=== NO TAB FORMAT VALIDATION ===");
                            console.log("All available tabs:", workbook.SheetNames);
                            console.log("No tabs_format provided, checking all tabs");
                        }
                        // Check headers in filtered tabs
                        for (const sheetName of tabsToCheck) {
                            console.log(`\n=== VALIDATING SHEET: "${sheetName}" ===`);
                            const worksheet = workbook.Sheets[sheetName];
                            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                                header: 1,
                            });
                            console.log(`Total rows in sheet: ${jsonData.length}`);
                            // Basic Excel validation: Check if sheet is empty
                            if (jsonData.length === 0) {
                                console.log("❌ VALIDATION FAILED: Sheet is completely empty");
                                resolve({
                                    isValid: false,
                                    error: `Sheet '${sheetName}' is empty`,
                                });
                                return;
                            }
                            // Basic Excel validation: Check if sheet has at least headers
                            if (jsonData.length < 1) {
                                console.log("❌ VALIDATION FAILED: Sheet has no header row");
                                resolve({
                                    isValid: false,
                                    error: `Sheet '${sheetName}' has no header row`,
                                });
                                return;
                            }
                            // Basic Excel validation: Check if sheet has data beyond headers
                            if (jsonData.length < 2) {
                                console.log("❌ VALIDATION FAILED: Sheet has headers but no data rows");
                                resolve({
                                    isValid: false,
                                    error: `Sheet '${sheetName}' has headers but no data rows. At least one data row is required.`,
                                });
                                return;
                            }
                            console.log("✅ Basic structure validation passed: Has headers and at least one data row");
                            const headers = jsonData[0] || [];
                            console.log(`Headers found: [${headers.map((h) => `"${h}"`).join(", ")}]`);
                            // Check for empty or invalid headers
                            const validHeaders = headers.filter((h) => h && h.toString().trim().length > 0);
                            if (validHeaders.length === 0) {
                                console.log("❌ VALIDATION FAILED: No valid headers found in first row");
                                resolve({
                                    isValid: false,
                                    error: `Sheet '${sheetName}' has no valid headers in the first row`,
                                });
                                return;
                            }
                            console.log(`Valid headers: [${validHeaders
                                .map((h) => `"${h}"`)
                                .join(", ")}]`);
                            const headerSet = new Set(headers.map((h) => h?.toString().trim().toLowerCase()));
                            // Only check mandatory headers if they are specified
                            if (validationOptions.mandatoryHeaders) {
                                console.log(`Required headers: [${mandatoryHeadersArray
                                    .map((h) => `"${h}"`)
                                    .join(", ")}]`);
                                const missingHeaders = mandatoryHeadersArray.filter((requiredHeader) => !headerSet.has(requiredHeader.toLowerCase()));
                                if (missingHeaders.length > 0) {
                                    console.log(`❌ VALIDATION FAILED: Missing required headers: [${missingHeaders
                                        .map((h) => `"${h}"`)
                                        .join(", ")}]`);
                                    resolve({
                                        isValid: false,
                                        error: `Missing required headers in sheet '${sheetName}': ${missingHeaders.join(", ")}`,
                                    });
                                    return;
                                }
                                console.log("✅ All mandatory headers found");
                            }
                            else {
                                console.log("No mandatory headers specified, skipping header validation");
                            }
                        }
                        console.log("🎉 ALL VALIDATIONS PASSED: File structure and headers are valid");
                        resolve({ isValid: true });
                    }
                    catch (error) {
                        resolve({
                            isValid: false,
                            error: `Error reading file: ${error instanceof Error ? error.message : "Unknown error"}`,
                        });
                    }
                };
                reader.onerror = () => {
                    resolve({
                        isValid: false,
                        error: "Error reading file",
                    });
                };
                reader.readAsBinaryString(file);
            });
        }
        catch (error) {
            return {
                isValid: false,
                error: `Validation error: ${error instanceof Error ? error.message : "Unknown error"}`,
            };
        }
    };
    useEffect(() => {
        console.log("File Data summary: ", fileSummary?.length);
    }, [fileSummary]);
    const initialized = useRef(false);
    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            // Add initial welcome message when chat begins for the first time
            setMessages([
                {
                    content: "Hi, I'm Dia, Scora's AI agent. Need help navigating features or creating assessments? I'm right here!",
                    role: "assistant",
                },
            ]);
            // Add loading message for the initial setup call
            const randomMessage = loadingPrompts[Math.floor(Math.random() * loadingPrompts.length)];
            setMessages((prevMessages) => [
                ...prevMessages,
                { content: randomMessage, role: "load" },
            ]);
            const uuid = crypto.randomUUID();
            getCopilotResponseStreaming({
                query: JSON.stringify({
                    query: "<start>",
                    accessToken: props.sessionVariables?.authorization,
                    org_id: props.sessionVariables?.Org_id,
                    author_id: props.sessionVariables?.Author_ID,
                }),
            }, uuid);
            setSessionId(uuid);
            // if (!sessionId) {
            //   createNewSession();
            // } else {
            //   getSession();
            // }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const getSession = async () => {
        if (sessionId) {
            try {
                const response = await copilotEngine.getSession(sessionId);
                console.log("Raw response: ", response.messages);
                // Ensure messages is properly parsed into an array
                let parsedMessages = [];
                try {
                    // First parse the string into JSON
                    const messagesData = JSON.parse(response?.messages);
                    // Check if it's already an array or needs another parse
                    if (Array.isArray(messagesData)) {
                        parsedMessages = messagesData;
                    }
                    else if (typeof messagesData === "string") {
                        // If it's still a string, parse again
                        parsedMessages = JSON.parse(messagesData);
                    }
                }
                catch (parseError) {
                    console.error("Error parsing messages:", parseError);
                    parsedMessages = []; // Fallback to empty array if parsing fails
                }
                console.log("Parsed Messages: ", parsedMessages);
                // Replace the initial loading message with the actual session messages
                // and add a new loading message for the upcoming response
                const randomMessage = loadingPrompts[Math.floor(Math.random() * loadingPrompts.length)];
                setMessages([
                    ...parsedMessages,
                    { content: randomMessage, role: "load" },
                ]);
                // Parse other data with error handling
                try {
                    const whitelistedURLs = JSON.parse(response?.whitelistedUrls || "[]");
                    if (whitelistedURLs.length > 0) {
                        setWhitelistedURLs(whitelistedURLs);
                    }
                    const loadingPromptsCustom = JSON.parse(response?.loadingPrompts || "[]");
                    if (loadingPromptsCustom.length > 0) {
                        setLoadingPrompts(loadingPromptsCustom);
                    }
                }
                catch (error) {
                    console.error("Error parsing additional data:", error);
                }
                // Get the initial response for existing session
                await getCopilotResponseStreaming({ query: "<start>" }, sessionId);
            }
            catch (error) {
                console.error("Error getting session:", error);
            }
        }
    };
    const createNewSession = async () => {
        try {
            console.log("Starting new session...");
            const uuid = crypto.randomUUID();
            setSessionId(uuid); // Set the session ID immediately
            getCopilotResponseStreaming({
                query: JSON.stringify({
                    query: "<start>",
                    accessToken: props.sessionVariables?.authorization,
                    org_id: props.sessionVariables?.Org_id,
                    author_id: props.sessionVariables?.Author_ID,
                }),
            }, uuid);
        }
        catch (error) {
            console.error("Error in createNewSession:", error);
        }
    };
    const updateSession = async (message) => {
        // Implement session update logic if needed
        //console.log(`Session ${sessionId} updated with message: ${message}`);
    };
    // Event handlers
    const toggleCopilot = () => {
        // wrapping in transaction to avoid error for sycnchronous request
        React.startTransition(() => {
            if (isOpen) {
                setIsOpen(false);
                setIsExpanded(false);
            }
            else {
                setIsOpen(true);
            }
        });
    };
    // Add drag event handlers for toggle button
    const onDragStart = useCallback((event) => {
        // Prevent dragging when copilot is open
        if (isOpen) {
            event.preventDefault();
            return;
        }
        event.stopPropagation();
        setIsDragging(true);
        setJustDragged(false);
        const rect = event.currentTarget.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;
        setDragOffset({ x: offsetX, y: offsetY });
        // Set drag data
        event.dataTransfer.setData("text/plain", "toggle-button");
        event.dataTransfer.effectAllowed = "move";
    }, [isOpen]);
    const onDragEnd = useCallback((event) => {
        // Prevent drag end processing when copilot is open
        if (isOpen) {
            return;
        }
        event.preventDefault();
        setIsDragging(false);
        setJustDragged(true);
        // Calculate new position relative to viewport
        const newX = event.clientX - dragOffset.x;
        const newY = event.clientY - dragOffset.y;
        // Ensure the button stays within viewport bounds
        const buttonSize = 60; // Approximate button size
        const maxX = window.innerWidth - buttonSize;
        const maxY = window.innerHeight - buttonSize;
        const clampedX = Math.max(0, Math.min(newX, maxX));
        const clampedY = Math.max(0, Math.min(newY, maxY));
        setTogglePosition({ x: clampedX, y: clampedY });
        // Clear the justDragged flag after a brief delay
        setTimeout(() => setJustDragged(false), 100);
    }, [dragOffset, isOpen]);
    const onDragOver = useCallback((event) => {
        // Prevent drag over when copilot is open
        if (isOpen) {
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    }, [isOpen]);
    const handleKeyDown = (event) => {
        if (event.key === "Enter" && input.trim()) {
            sendMessage();
        }
    };
    // Update handleFileChange to enforce validation rules
    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check file size first
            if (file.size > MAX_FILE_SIZE) {
                showFileError("File size exceeds 25MB limit.");
                if (fileInputRef.current)
                    fileInputRef.current.value = "";
                return;
            }
            // Apply validation rules if they exist from button configuration
            if (fileValidationOptions) {
                console.log("Applying file validation options:", fileValidationOptions);
                const validationResult = await validateFileHeaders(file, fileValidationOptions);
                if (!validationResult.isValid) {
                    // Use server-provided error message if available, otherwise use validation-specific error
                    const errorMessage = fileValidationOptions.errorMessage ||
                        validationResult.error ||
                        "File validation failed.";
                    showFileError(errorMessage);
                    if (fileInputRef.current)
                        fileInputRef.current.value = "";
                    // Clear validation options and pending prompt
                    setFileValidationOptions(null);
                    setPendingButtonPrompt(null);
                    return;
                }
                console.log("File validation passed");
            }
            else {
                // Fallback to default validation when no button validation options
                const allowedTypes = ["application/pdf"];
                const allowedExtensions = [".pdf", ".xlsx", ".docx"];
                if (!allowedTypes.includes(file.type) &&
                    !allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))) {
                    showFileError("Only PDF, and XLSX files are allowed.");
                    if (fileInputRef.current)
                        fileInputRef.current.value = "";
                    return;
                }
            }
            // setFileData(file);
            // setShowFilePreview(true);
            setDocRemoveAfterAssistantMessages(null); // Reset counter on new upload
            getDocumentSummary(file)
                .then((summary) => {
                console.log("Document summary result:", summary);
                console.log("Pending button prompt:", pendingButtonPrompt);
                if (summary == null || summary === undefined) {
                    console.warn("Summary is null/undefined, aborting prompt send");
                    if (fileInputRef.current)
                        fileInputRef.current.value = "";
                    return;
                }
                // If there's a pending button prompt, send it automatically after file processing
                if (pendingButtonPrompt) {
                    console.log("Sending pending button prompt:", pendingButtonPrompt);
                    const isHiddenMessage = pendingButtonPrompt.startsWith("#") &&
                        pendingButtonPrompt.endsWith("#");
                    if (!isHiddenMessage) {
                        // Only add to UI if it's not a hidden message
                        const userMessage = {
                            content: pendingButtonPrompt,
                            role: "user",
                            fileData: {
                                name: file.name,
                                size: file.size,
                                data: file,
                                summary: summary,
                            },
                        };
                        console.log("Adding user message to chat:", userMessage);
                        setMessages((prevMessages) => [...prevMessages, userMessage]);
                    }
                    console.log("About to call getCopilotResponseStreaming with query:", pendingButtonPrompt);
                    getCopilotResponseStreaming({ query: pendingButtonPrompt });
                    setPendingButtonPrompt(null); // Clear the pending prompt
                    setFileValidationOptions(null); // Clear validation options
                    console.log("Cleared pending button prompt and validation options");
                }
                else {
                    console.log("No pending button prompt to send");
                }
            })
                .catch((error) => {
                // Quietly handle errors - clear pending prompt and exit gracefully
                console.warn("Document summary failed:", error);
                console.log("Pending button prompt during error:", pendingButtonPrompt);
                if (pendingButtonPrompt) {
                    console.log("Clearing pending button prompt due to error");
                    setPendingButtonPrompt(null);
                    setFileValidationOptions(null);
                }
                // Remove the loading message without replacing it with an error
                setMessages((prevMessages) => {
                    const newMessages = [...prevMessages];
                    const lastIndex = newMessages.length - 1;
                    if (lastIndex >= 0 &&
                        newMessages[lastIndex].content === "file_upload_loading") {
                        newMessages.pop();
                    }
                    return newMessages;
                });
            });
        }
        else if (pendingButtonPrompt) {
            // If no file was selected but there's a pending prompt (user cancelled), clear it
            setPendingButtonPrompt(null);
            setFileValidationOptions(null);
        }
    };
    // Update getDocumentSummary for new progress logic
    const getDocumentSummary = async (file) => {
        try {
            if (!file)
                return;
            setIsFileUploading(true);
            setFileUploadProgress(0);
            // Add upload loading message to chat
            setMessages((prevMessages) => [
                ...prevMessages,
                { content: "file_upload_loading", role: "load" },
            ]);
            let progress = 0;
            const progressInterval = setInterval(() => {
                progress += 25;
                if (progress < 100) {
                    setFileUploadProgress(progress);
                }
            }, 5000);
            const formData = new FormData();
            formData.append("file", file);
            // Add session ID so backend can update session variable memory
            if (sessionId) {
                formData.append("session_id", sessionId);
            }
            // Check for custom upload link from button
            const customUploadLink = localStorage.getItem("customUploadLink");
            const uploadUrl = customUploadLink ||
                `${process.env.REACT_APP_BACKEND_URL}/chat/document-summary/`;
            console.log("Upload URL:", uploadUrl);
            console.log("Is custom upload:", !!customUploadLink);
            const response = await fetch(uploadUrl, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${props.sessionVariables?.authorization ||
                        localStorage.getItem("accessToken")}`,
                },
                body: formData,
            });
            // Clear the custom upload link after use
            localStorage.removeItem("customUploadLink");
            clearInterval(progressInterval);
            setFileUploadProgress(100);
            let data;
            try {
                data = await response.json();
                console.log("Upload response data:", data);
            }
            catch (jsonError) {
                console.warn("Failed to parse JSON response, treating as success:", jsonError);
                // If JSON parsing fails but status is OK, treat as successful upload
                data = { success: true };
            }
            if (!response.ok) {
                setIsFileUploading(false);
                // Check for server error message in response data
                const serverError = data?.error || data?.message || response.statusText;
                throw new Error(serverError);
            }
            // Check for error in successful response (some APIs return 200 with error field)
            if (data?.error) {
                setIsFileUploading(false);
                throw new Error(data.error);
            }
            // Handle different response formats for custom upload links
            // Custom endpoints might not return a 'summary' field
            const summary = data?.summary ||
                data?.message ||
                data?.result ||
                data?.data ||
                "File uploaded successfully";
            setFileSummary(summary);
            // Replace loading message with success message
            setMessages((prevMessages) => {
                const newMessages = [...prevMessages];
                // Replace the last loading message with success message
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 &&
                    newMessages[lastIndex].content === "file_upload_loading") {
                    newMessages[lastIndex] = {
                        content: "**Done!**",
                        role: "assistant",
                        fileData: {
                            name: file.name,
                            size: file.size,
                            data: file,
                            summary: summary,
                        },
                    };
                }
                else {
                    // Fallback: just add the success message
                    newMessages.push({
                        content: "**Done!**",
                        role: "assistant",
                        fileData: {
                            name: file.name,
                            size: file.size,
                            data: file,
                            summary: summary,
                        },
                    });
                }
                return newMessages;
            });
            setTimeout(() => {
                setIsFileUploading(false);
                setFileUploadProgress(0);
                // Send file uploaded message to server after successful upload
                // getCopilotResponseStreaming({ query: "#File Uploaded#" });
            }, 500); // Hide after short delay
            return summary;
        }
        catch (error) {
            setIsFileUploading(false);
            setFileUploadProgress(0);
            // Remove the loading message without replacing it with an error
            setMessages((prevMessages) => {
                const newMessages = [...prevMessages];
                const lastIndex = newMessages.length - 1;
                if (lastIndex >= 0 &&
                    newMessages[lastIndex].content === "file_upload_loading") {
                    // Just remove the loading message
                    newMessages.pop();
                }
                return newMessages;
            });
            // Clear pending button prompt if upload fails
            if (pendingButtonPrompt) {
                setPendingButtonPrompt(null);
            }
            console.error("Error getting document summary:", error);
            // Display server error message if available, otherwise fallback to generic message
            const errorMessage = error instanceof Error
                ? error.message
                : "Something went wrong, Please try again.";
            showFileError(errorMessage);
            return null;
        }
    };
    // Update the handleSendClick function to handle file uploads
    const handleSendClick = () => {
        if (input.trim()) {
            sendMessage();
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };
    // Modify the sendMessage function to include file data in regular messages
    const sendMessage = () => {
        const isHiddenMessage = input.startsWith("#") && input.endsWith("#");
        if (!isHiddenMessage) {
            // Only add to UI if it's not a hidden message
            const newMessage = {
                content: input,
                role: "user",
                fileData: fileData
                    ? {
                        name: fileData.name,
                        size: fileData.size,
                        data: fileData,
                        summary: fileSummary,
                    }
                    : null,
            };
            setMessages((prevMessages) => [...prevMessages, newMessage]);
        }
        updateSession(input);
        // Note: getCopilotResponseStreaming handles the main response
        // Suggestions should be fetched separately using getSuggestions() calls where needed
        getCopilotResponseStreaming({
            query: JSON.stringify({
                query: input,
                accessToken: props.sessionVariables?.authorization,
                org_id: props.sessionVariables?.Org_id,
                author_id: props.sessionVariables?.Author_ID,
            }),
        });
        setInput("");
        //setFileData(null);
        setShowFilePreview(false); // Hide file preview when message is sent
        if (!hasUserInteracted) {
            setHasUserInteracted(true);
        }
    };
    const sendDisabledMessage = () => {
        if (messages.length == 0) {
            return false;
        }
        else {
            return messages[messages.length - 1].role != "assistant";
        }
    };
    /*
    const getCopilotResponse = async (userInput: string) => {
      if (sessionId) {
        try {
          const response = await copilotEngine.getResponse(sessionId, userInput);
          //console.log(response);
          const responseData = JSON.parse(response?.data?.getResponse?.response);
          const responseTour = responseData?.ui_tour;
          const suggestions = responseData?.suggestions;
  
          if (responseData) {
            if (suggestions) {
              // Convert suggestions string to array
              const suggestionsArray = typeof suggestions === 'string' ? suggestions.split(',').map(s => s.trim()) : Array.isArray(suggestions) ? suggestions : [suggestions];
              setSuggestions(suggestionsArray);
            }
            if (responseTour) {
              //console.log('ResponseTour: ', responseTour);
              const renderedTour = renderTour(responseTour);
              //console.log('RenderedTour: ', renderedTour);
              setTourSteps([...renderedTour]);
              setIsWalkthroughReady(true);
            }
            const botMessage = {
              content: responseData.response,
              role: 'assistant' as const,
            };
            setMessages((prevMessages) => [...prevMessages, botMessage]);
            updateSession(botMessage.content);
          } else {
            throw new Error('Invalid response structure');
          }
        } catch (error) {
          console.error('Error getting response:', error);
          const errorMessage = {
            content: `Sorry, I encountered an error: ${error}. Please try again later.`,
            role: 'assistant' as const,
          };
          setMessages((prevMessages) => [...prevMessages, errorMessage]);
        }
      } else {
        console.error('No active session');
        const noSessionMessage = {
          content: "Sorry, there's no active session. Please try refreshing the page.",
          role: 'assistant' as const,
        };
        setMessages((prevMessages) => [...prevMessages, noSessionMessage]);
      }
    };
  
    */
    // Choose a random message from the array
    const randomMessage = loadingPrompts[Math.floor(Math.random() * loadingPrompts.length)];
    const getCopilotResponseStreaming = async (user_response, overrideSessionId) => {
        console.log("Getting streaming response");
        console.log("overrideSessionId:", overrideSessionId);
        console.log("sessionId state:", sessionId);
        const currentSessionId = overrideSessionId || sessionId || "abcd";
        console.log("currentSessionId (final):", currentSessionId);
        console.log("Message: ", user_response);
        if (currentSessionId) {
            try {
                // Check if this is the initial session setup call
                const isInitialSetup = user_response.query === "<start>";
                // Only add loading message if it's not the initial setup (to avoid duplicate loading messages)
                if (!isInitialSetup) {
                    setMessages((prevMessages) => [
                        ...prevMessages,
                        { content: randomMessage, role: "load" },
                    ]);
                }
                let fullResponse = "";
                let hasReplacedLoadingMessage = false;
                // Extract file data from user_response and remove it from what gets sent to the API
                const { ...cleanUserResponse } = user_response;
                // Helper function to process individual batch items
                const processBatchItem = (mockResponse, hasReplacedLoadingMessage) => {
                    let didReplaceMessage = false;
                    switch (mockResponse.type) {
                        case "blueprint":
                            if (mockResponse.data.blueprint) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    // Remove the loading message if it hasn't been replaced yet
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages.pop();
                                    }
                                    const newMessage = {
                                        content: `blueprint:` + JSON.stringify(mockResponse.data.blueprint),
                                        role: "assistant",
                                    };
                                    console.log("New blueprint message:", newMessage);
                                    // Add title message, then the blueprint content message
                                    return [
                                        ...newMessages,
                                        {
                                            content: "Configure your question blueprint by selecting topics and setting parameters.",
                                            role: "assistant",
                                        },
                                        newMessage,
                                    ];
                                });
                                hasReplacedLoadingMessage = true;
                                handleAssistantMessage({
                                    content: `blueprint:${JSON.stringify(mockResponse.data.blueprint)}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "token":
                            if (mockResponse.data.token) {
                                if (mockResponse.data.is_last) {
                                    // If it's the last token of a batch item (or standalone), 
                                    // we should replace the loading message if it hasn't been replaced yet.
                                    setMessages((prevMessages) => {
                                        const newMessages = [...prevMessages];
                                        const newMessage = {
                                            content: mockResponse.data.token || "",
                                            role: "assistant",
                                            fileData: {
                                                name: fileData?.name || "",
                                                size: fileData?.size || 0,
                                                data: fileData || null,
                                                summary: fileSummary || null,
                                            },
                                        };
                                        if (!hasReplacedLoadingMessage) {
                                            // Replace loading message
                                            if (newMessages.length > 0) {
                                                newMessages[newMessages.length - 1] = newMessage;
                                            }
                                            else {
                                                newMessages.push(newMessage);
                                            }
                                            didReplaceMessage = true;
                                        }
                                        else {
                                            // Append as new message
                                            newMessages.push(newMessage);
                                        }
                                        return newMessages;
                                    });
                                }
                                else {
                                    // Standard token streaming (update existing message)
                                    let tokenContent = mockResponse.data.token || "";
                                    if (mockResponse.data.tooltip) {
                                        const encodedTooltip = encodeURIComponent(mockResponse.data.tooltip);
                                        tokenContent = `[${tokenContent}](tooltip:${encodedTooltip})`;
                                    }
                                    fullResponse += tokenContent;
                                    setMessages((prevMessages) => {
                                        const newMessages = [...prevMessages];
                                        if (!hasReplacedLoadingMessage) {
                                            newMessages[newMessages.length - 1] = {
                                                content: fullResponse,
                                                role: "assistant",
                                            };
                                            didReplaceMessage = true;
                                        }
                                        else {
                                            // Update the last message if already replaced (streaming continuation)
                                            // Instead of pushing a NEW message for every token, we update the last one.
                                            // Note: This relies on fullResponse being correct.
                                            if (newMessages.length > 0) {
                                                newMessages[newMessages.length - 1] = {
                                                    ...newMessages[newMessages.length - 1],
                                                    content: fullResponse
                                                };
                                            }
                                        }
                                        return newMessages;
                                    });
                                }
                            }
                            break;
                        case "form":
                            if (mockResponse.data.form) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `form:` + JSON.stringify(mockResponse.data.form),
                                        role: "assistant",
                                    };
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                        didReplaceMessage = true;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                handleAssistantMessage({
                                    content: `form:${JSON.stringify(mockResponse.data.form)}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "tags":
                            if (mockResponse.data.tags) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `tags:` + JSON.stringify(mockResponse.data.tags),
                                        role: "assistant",
                                    };
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                        didReplaceMessage = true;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                handleAssistantMessage({
                                    content: `tags:${JSON.stringify(mockResponse.data.tags)}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "buttons":
                            if (mockResponse.data.buttons) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `buttons:` + JSON.stringify(mockResponse.data.buttons),
                                        role: "assistant",
                                    };
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                        didReplaceMessage = true;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                handleAssistantMessage({
                                    content: `buttons:${mockResponse.data.buttons}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "button":
                            if (mockResponse.data.button) {
                                // Convert single button to buttons array format
                                const buttonsArray = [mockResponse.data.button];
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `buttons:` + JSON.stringify(buttonsArray),
                                        role: "assistant",
                                    };
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                        didReplaceMessage = true;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                handleAssistantMessage({
                                    content: `buttons:${JSON.stringify(buttonsArray)}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "k_cards":
                            if (mockResponse.data.k_cards) {
                                setIsChatDisabled(true);
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `k_cards:` + JSON.stringify(mockResponse.data.k_cards),
                                        role: "assistant",
                                    };
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                        didReplaceMessage = true;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                handleAssistantMessage({
                                    content: `k_cards:` + JSON.stringify(mockResponse.data.k_cards),
                                    role: "assistant",
                                });
                            }
                            break;
                        case "uploader":
                            if (mockResponse.data.uploader) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `uploader:` + JSON.stringify(mockResponse.data.uploader),
                                        role: "assistant",
                                    };
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                        didReplaceMessage = true;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                handleAssistantMessage({
                                    content: `uploader:` + JSON.stringify(mockResponse.data.uploader),
                                    role: "assistant",
                                });
                            }
                            break;
                        default:
                            break;
                    }
                    return didReplaceMessage;
                };
                copilotEngine.getStreamingResponse(currentSessionId, cleanUserResponse, (response) => {
                    console.log("Response: ", response);
                    switch (response.type) {
                        case "blueprint":
                            if (response.data.blueprint) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    // Remove the loading message if it hasn't been replaced yet
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages.pop();
                                    }
                                    const newMessage = {
                                        content: `blueprint:` + JSON.stringify(response.data.blueprint),
                                        role: "assistant",
                                    };
                                    console.log("New blueprint message:", newMessage);
                                    // Add title message, then the blueprint content message
                                    return [
                                        ...newMessages,
                                        {
                                            content: "Configure your question blueprint by selecting topics and setting parameters.",
                                            role: "assistant",
                                        },
                                        newMessage,
                                    ];
                                });
                                hasReplacedLoadingMessage = true;
                                handleAssistantMessage({
                                    content: `blueprint:${JSON.stringify(response.data.blueprint)}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "loader":
                            if (response.data.loader) {
                                console.log("Received Loader:", response.data.loader);
                                setActiveLoader(response.data.loader);
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    // Remove the loading message if it hasn't been replaced yet
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages.pop();
                                    }
                                    hasReplacedLoadingMessage = true;
                                    return [
                                        ...newMessages,
                                        {
                                            role: 'assistant',
                                            content: `loader:${JSON.stringify(response.data.loader)}`
                                        }
                                    ];
                                });
                                // Also trigger onAssistantMessage if needed for history
                                handleAssistantMessage({
                                    content: `loader:${JSON.stringify(response.data.loader)}`,
                                    role: "assistant"
                                });
                            }
                            break;
                        case "token":
                            if (response.data.token) {
                                if (response.data.is_last && response.data.token) {
                                    setMessages((prevMessages) => {
                                        const newMessages = [...prevMessages];
                                        newMessages.push({
                                            content: response.data.token || "",
                                            role: "assistant",
                                            fileData: {
                                                name: fileData?.name || "",
                                                size: fileData?.size || 0,
                                                data: fileData || null,
                                                summary: fileSummary || null,
                                            },
                                        });
                                        return newMessages;
                                    });
                                    hasReplacedLoadingMessage = true;
                                    break;
                                }
                                // Only update if it's not a form message
                                if (!fullResponse.startsWith("form:")) {
                                    let tokenContent = response.data.token || "";
                                    if (response.data.tooltip) {
                                        // Format as markdown link with custom protocol
                                        const encodedTooltip = encodeURIComponent(response.data.tooltip);
                                        tokenContent = `[${tokenContent}](tooltip:${encodedTooltip})`;
                                    }
                                    fullResponse += tokenContent;
                                    setMessages((prevMessages) => {
                                        const newMessages = [...prevMessages];
                                        if (fullResponse.trim()) {
                                            newMessages[newMessages.length - 1] = {
                                                content: fullResponse,
                                                role: "assistant",
                                                fileData: {
                                                    name: fileData?.name || "",
                                                    size: fileData?.size || 0,
                                                    data: fileData || null,
                                                    summary: fileSummary || null,
                                                },
                                            };
                                            hasReplacedLoadingMessage = true;
                                        }
                                        return newMessages;
                                    });
                                    handleAssistantMessage({
                                        content: fullResponse,
                                        role: "assistant",
                                    });
                                }
                            }
                            break;
                        case "form":
                            // When receiving a form response, replace loading message and add form messages
                            if (response.data?.form) {
                                console.log("Received Form:", response.data.form);
                                const formRaw = response.data.form;
                                const formData = typeof formRaw === "string" ? JSON.parse(formRaw) : formRaw;
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    // Remove the loading message if it hasn't been replaced yet
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages.pop();
                                    }
                                    // Add form messages
                                    return [
                                        ...newMessages,
                                        {
                                            content: formData.form_title,
                                            role: "assistant",
                                        },
                                        {
                                            content: `form:` + JSON.stringify(formData),
                                            role: "assistant",
                                            fileData: fileData
                                                ? {
                                                    name: fileData.name,
                                                    size: fileData.size,
                                                    data: fileData,
                                                    summary: fileSummary,
                                                }
                                                : null,
                                        },
                                    ];
                                });
                                hasReplacedLoadingMessage = true;
                                handleAssistantMessage({
                                    content: `form:` + JSON.stringify(formData),
                                    role: "assistant",
                                });
                                fullResponse = `form:` + JSON.stringify(formData); // mark as form response
                            }
                            break;
                        case "tags":
                            if (response.data.tags) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    // Remove the loading message if it hasn't been replaced yet
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages.pop();
                                    }
                                    const newMessage = {
                                        content: `tags:` + JSON.stringify(response.data.tags),
                                        role: "assistant",
                                    };
                                    console.log("New tags message:", newMessage);
                                    // Add title message, then the tags content message
                                    return [
                                        ...newMessages,
                                        {
                                            content: "Below are the generated tags. Please review and submit them.",
                                            role: "assistant",
                                        },
                                        newMessage,
                                    ];
                                });
                                hasReplacedLoadingMessage = true;
                                handleAssistantMessage({
                                    content: `tags:${JSON.stringify(response.data.tags)}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "suggestions":
                            // Note: We intentionally don't set suggestions from streaming response
                            // to ensure we always fetch fresh suggestions via getSuggestions() calls
                            console.log("Suggestions received in streaming response, but we'll fetch fresh ones separately");
                            break;
                        case "buttons":
                            if (response.data.buttons) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `buttons:` + JSON.stringify(response.data.buttons),
                                        role: "assistant",
                                    };
                                    // Replace the loading message with the buttons message
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                hasReplacedLoadingMessage = true;
                                handleAssistantMessage({
                                    content: `buttons:${response.data.buttons}`,
                                    role: "assistant",
                                });
                            }
                            break;
                        case "k_cards":
                            if (response.data.k_cards) {
                                // Reset chat disabled state when k_cards are received
                                setIsChatDisabled(true);
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `k_cards:` + JSON.stringify(response.data.k_cards),
                                        role: "assistant",
                                    };
                                    // Replace the loading message with the feature cards message
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                hasReplacedLoadingMessage = true;
                                handleAssistantMessage({
                                    content: `k_cards:` + JSON.stringify(response.data.k_cards),
                                    role: "assistant",
                                });
                            }
                            break;
                        case "uploader":
                            if (response.data.uploader) {
                                setMessages((prevMessages) => {
                                    const newMessages = [...prevMessages];
                                    const newMessage = {
                                        content: `uploader:` + JSON.stringify(response.data.uploader),
                                        role: "assistant",
                                    };
                                    // Replace the loading message with the uploader message
                                    if (!hasReplacedLoadingMessage) {
                                        newMessages[newMessages.length - 1] = newMessage;
                                    }
                                    else {
                                        newMessages.push(newMessage);
                                    }
                                    return newMessages;
                                });
                                hasReplacedLoadingMessage = true;
                                handleAssistantMessage({
                                    content: `uploader:` + JSON.stringify(response.data.uploader),
                                    role: "assistant",
                                });
                            }
                            break;
                        case "api_call":
                            console.log("API Call Line 399: ", response.data.api_call, typeof response.data.api_call);
                            if (response.data.api_call) {
                                let api_data;
                                if (typeof response.data.api_call === "string") {
                                    api_data = JSON.parse(response.data.api_call);
                                }
                                else {
                                    api_data = response.data.api_call;
                                }
                                console.log("API Call: ", api_data);
                                // Check if we have a file to upload
                                if (fileData) {
                                    // Create FormData for file upload
                                    const formData = new FormData();
                                    // Add all form fields from the API data body
                                    if (api_data.body) {
                                        Object.entries(api_data.body).forEach(([key, value]) => {
                                            formData.append(key, String(value));
                                        });
                                    }
                                    // Add the file
                                    formData.append("file", fileData);
                                    // Make the fetch request with FormData
                                    fetch(api_data.url, {
                                        method: api_data.method,
                                        headers: {
                                            // Don't set Content-Type as browser will set it automatically with boundary
                                            ...api_data.headers,
                                        },
                                        body: formData,
                                    })
                                        .then((response) => response.json())
                                        .then((data) => {
                                        console.log("API Response with file: ", data);
                                        setMessages((prevMessages) => {
                                            const newMessages = [...prevMessages];
                                            newMessages[newMessages.length - 1] = {
                                                content: "Your file has been successfully processed.",
                                                role: "assistant",
                                            };
                                            return newMessages;
                                        });
                                    })
                                        .catch((error) => {
                                        setMessages((prevMessages) => {
                                            const newMessages = [...prevMessages];
                                            newMessages[newMessages.length - 1] = {
                                                content: "Upload failed. Your file couldn't be processed.",
                                                role: "assistant",
                                            };
                                            return newMessages;
                                        });
                                        console.error("API Error: ", error);
                                    });
                                }
                                else {
                                    // Standard JSON request without file
                                    fetch(api_data.url, {
                                        method: api_data.method,
                                        headers: {
                                            "Content-Type": "application/json",
                                            ...api_data.headers,
                                        },
                                        body: JSON.stringify(api_data.body),
                                    })
                                        .then((response) => response.json())
                                        .then((data) => {
                                        console.log("API Response: ", data);
                                        setMessages((prevMessages) => {
                                            const newMessages = [...prevMessages];
                                            newMessages[newMessages.length - 1] = {
                                                content: "Your items have been successfully uploaded.",
                                                role: "assistant",
                                            };
                                            return newMessages;
                                        });
                                    })
                                        .catch((error) => {
                                        setMessages((prevMessages) => {
                                            const newMessages = [...prevMessages];
                                            newMessages[newMessages.length - 1] = {
                                                content: "Upload failed. Your items couldn't be submitted.",
                                                role: "assistant",
                                            };
                                            return newMessages;
                                        });
                                        console.error("API Error: ", error);
                                    });
                                }
                            }
                            break;
                        case "batch":
                            if (response.data.batch && Array.isArray(response.data.batch)) {
                                // Process each item in the batch array
                                let batchHasReplacedLoadingMessage = hasReplacedLoadingMessage;
                                response.data.batch.forEach((batchItem) => {
                                    // Each batch item is an object with one key-value pair
                                    Object.keys(batchItem).forEach((key) => {
                                        // Create a mock response object for each batch item
                                        let data = { [key]: batchItem[key] };
                                        // Special handling for tokens to include tooltip and is_last
                                        if (key === 'token') {
                                            data = {
                                                token: batchItem.token,
                                                tooltip: batchItem.tooltip,
                                                is_last: batchItem.is_last
                                            };
                                        }
                                        const mockResponse = {
                                            type: key,
                                            data: data,
                                        };
                                        // Process each item and update the flag
                                        const itemReplacedMessage = processBatchItem(mockResponse, batchHasReplacedLoadingMessage);
                                        if (itemReplacedMessage) {
                                            batchHasReplacedLoadingMessage = true;
                                            hasReplacedLoadingMessage = true;
                                        }
                                    });
                                });
                            }
                            break;
                        case "error":
                            // setMessages((prevMessages) => {
                            //   const newMessages = [...prevMessages];
                            //   newMessages[newMessages.length - 1] = {
                            //     content: response.data.token || "Unknown error",
                            //     role: "assistant" as const,
                            //   };
                            //   return newMessages;
                            // });
                            break;
                    }
                });
                updateSession(fullResponse);
            }
            catch (error) {
                console.error("Error getting response:", error);
                setMessages((prevMessages) => [
                    ...prevMessages,
                    {
                        content: `Sorry, I encountered an error: ${error}. Please try again later.`,
                        role: "assistant",
                    },
                ]);
            }
        }
    };
    const handleStartWalkthrough = () => {
        setIsWalkthroughReady(false);
        setIsWalkthroughActive(true);
    };
    const handleFinishWalkthrough = () => {
        setIsWalkthroughActive(false);
    };
    // Smart scrolling to show new messages at the top of visible area
    const smartScrollToNewMessages = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container)
            return;
        // Find the first unseen message (first message after lastSeenMessageIndex)
        const firstUnseenIndex = lastSeenMessageIndex + 1;
        // Skip loading messages when determining scroll target, unless it's the only new message
        let targetIndex = firstUnseenIndex;
        const nonLoadingMessages = messages
            .slice(firstUnseenIndex)
            .filter((msg) => msg.role !== "load");
        if (nonLoadingMessages.length > 0) {
            // Find first non-loading message
            targetIndex = messages.findIndex((msg, idx) => idx >= firstUnseenIndex && msg.role !== "load");
        }
        else if (firstUnseenIndex < messages.length) {
            // If only loading messages are new, scroll to the first one
            targetIndex = firstUnseenIndex;
        }
        else {
            return; // No new messages to scroll to
        }
        // Ensure target index is valid
        if (targetIndex < 0 || targetIndex >= messages.length)
            return;
        const targetMessageRef = messageRefs.current.get(targetIndex);
        if (targetMessageRef) {
            // Scroll to position the target message at the top of the visible area
            targetMessageRef.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [lastSeenMessageIndex, messages]);
    // Update last seen message index when messages change
    useEffect(() => {
        if (messages.length === 0) {
            setLastSeenMessageIndex(-1);
            setMessagesBeforeNewBatch(0);
            return;
        }
        // When this is the first time messages are loaded (session initialization)
        if (lastSeenMessageIndex === -1 && messages.length > 0) {
            // For initial load, don't scroll automatically, let the fallback handle it
            setLastSeenMessageIndex(messages.length - 1);
            setMessagesBeforeNewBatch(messages.length);
            return;
        }
        // Check if new messages were added (length increased)
        if (messages.length > messagesBeforeNewBatch) {
            // New messages were added, scroll to show them
            requestAnimationFrame(() => {
                smartScrollToNewMessages();
            });
            // Update the last seen index to just before the new batch
            setLastSeenMessageIndex(messagesBeforeNewBatch - 1);
        }
        else if (messages.length === messagesBeforeNewBatch) {
            // Same number of messages, might be an update to existing message (streaming)
            // For streaming updates, we don't change the lastSeenMessageIndex
            // The scroll position should remain stable for streaming content
        }
        // Update the count for next comparison
        setMessagesBeforeNewBatch(messages.length);
    }, [
        messages,
        smartScrollToNewMessages,
        lastSeenMessageIndex,
        messagesBeforeNewBatch,
    ]);
    // Fallback scrolling for initial load and edge cases
    useEffect(() => {
        console.log("Messages: ", messages);
        // Only use fallback scrolling for very first message or when smart scrolling system isn't active
        if (messages.length === 1 && lastSeenMessageIndex === -1) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
        }
    }, [messages, lastSeenMessageIndex]);
    // Measure last message width for button alignment
    useEffect(() => {
        if (lastMessageRef.current) {
            const width = lastMessageRef.current.offsetWidth;
            console.log("Last message width: ", width);
            setLastMessageWidth(width);
        }
    }, [messages]);
    const adjustTextareaHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            if (input === "") {
                // Reset to minimum height when input is empty
                textarea.style.height = "48px";
            }
            else {
                // Reset height to recalculate
                textarea.style.height = "48px";
                textarea.style.height = `${Math.max(textarea.scrollHeight, 48)}px`;
            }
        }
    }, [input, isOpen]);
    useEffect(() => {
        adjustTextareaHeight();
    }, [input, adjustTextareaHeight]);
    const handleInputChange = (e) => {
        setInput(e.target.value);
        if (showStartingUserMessages) {
            setShowStartingUserMessages(false);
        }
    };
    const handleResetChat = async () => {
        setIsResetting(true);
        if (sessionId) {
            await copilotEngine.endSession(sessionId);
        }
        // Reset all states before creating new session
        resetAllStates();
        // Add initial welcome message after reset
        setMessages([
            {
                content: "Hi, I'm Dia, Scora's AI agent. Need help navigating features or creating assessments? I'm right here!",
                role: "assistant",
            },
        ]);
        await createNewSession();
        setIsResetting(false);
    };
    // Function to register message refs for smart scrolling
    const registerMessageRef = useCallback((index, element) => {
        if (element) {
            messageRefs.current.set(index, element);
        }
        else {
            messageRefs.current.delete(index);
        }
    }, []);
    // Function to reset all useState variables when session ends
    const resetAllStates = () => {
        // Reset conversation and input state
        setMessages([]);
        setInput("");
        // Reset suggestions and user interaction state
        setSuggestions([]);
        setHasUserInteracted(false);
        setSessionId(null);
        // Reset chat session ID to a new random ID
        const newResetSessionId = "session_" + Math.random().toString(36).substr(2, 16);
        console.log("🔄 Chat session ID reset to new ID:", newResetSessionId);
        setChatSessionId(newResetSessionId);
        // Reset file-related states
        setFileData(null);
        setFileError("");
        setServerErrorMessage("");
        setShowFilePreview(false);
        setFileSummary(null);
        setFileUploadProgress(0);
        setIsFileUploading(false);
        setDocRemoveAfterAssistantMessages(null);
        setPendingButtonPrompt(null);
        setIsFileButtonHovered(false);
        setFileValidationOptions(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        // Reset tags and form states
        setTags([]);
        setClickedButtons({});
        setSelectedFeatureTemplates({});
        // Reset UI states
        setIsChatDisabled(true);
        setShowStartingUserMessages(false);
        setIsWalkthroughReady(false);
        setIsWalkthroughActive(false);
        setTourSteps([]);
        // Reset scrolling states
        setLastMessageWidth(0);
        // Reset smart scrolling states
        setLastSeenMessageIndex(-1);
        setMessagesBeforeNewBatch(0);
        messageRefs.current.clear();
        pendingScrollToIndex.current = null;
        // Reset starting user messages
        setStartingUserMessages([]);
        // Reset loading prompts to default
        setLoadingPrompts(loadingPropmtOptionsDefault);
        // Reset feature templates
        setFeatureTemplates([]);
        // Reset whitelisted URLs
        setWhitelistedURLs([]);
    };
    const handleCloseChat = () => {
        setIsOpen(false);
        setIsExpanded(false);
    };
    const handleSuggestionClick = (suggestion) => {
        const newMessage = {
            content: suggestion,
            role: "user",
        };
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        // Clear suggestions when one is clicked
        setSuggestions([]);
        getCopilotResponseStreaming({ query: suggestion });
        if (!hasUserInteracted) {
            setHasUserInteracted(true);
        }
    };
    const handleExpandClick = () => {
        setIsExpanded(!isExpanded);
    };
    // Styles (same as your original code)
    const defaultStyles = CopilotStyles;
    const styles = {
        ...defaultStyles,
    };
    const components = {
        h1: ({ node, ...props }) => _jsx("h1", { style: styles.markdownH1, ...props }),
        h2: ({ node, ...props }) => _jsx("h2", { style: styles.markdownH2, ...props }),
        h3: ({ node, ...props }) => _jsx("h3", { style: styles.markdownH3, ...props }),
        h4: ({ node, ...props }) => _jsx("h4", { style: styles.markdownH4, ...props }),
        h5: ({ node, ...props }) => _jsx("h5", { style: styles.markdownH5, ...props }),
        h6: ({ node, ...props }) => _jsx("h6", { style: styles.markdownH6, ...props }),
        p: ({ node, children, ...props }) => (_jsx("p", { style: { ...styles.markdownP, whiteSpace: "normal" }, ...props, children: children })),
        strong: ({ node, ...props }) => (_jsx("strong", { style: { color: "#0E497A" }, ...props })),
        code({ node, className, children, ...props }) {
            const codeContent = String(children).replace(/\n$/, "");
            const hasLanguage = className && className.startsWith("language-");
            const handleCopy = () => {
                navigator.clipboard.writeText(codeContent);
                // Optionally, you can show a tooltip or change button text to "Copied!"
            };
            return hasLanguage ? (_jsxs("div", { style: styles.codeBlockWrapper, children: [_jsx("pre", { style: styles.codeBlock, children: _jsx("code", { className: className, ...props, children: codeContent }) }), _jsx("button", { style: styles.copyButton, onClick: handleCopy, children: "Copy" })] })) : (_jsx("code", { style: styles.inlineCode, ...props, children: children }));
        },
        table: ({ node, ...props }) => (_jsx("table", { style: styles.markdownTable, ...props })),
        thead: ({ node, ...props }) => (_jsx("thead", { style: styles.markdownThead, ...props })),
        tbody: ({ node, ...props }) => (_jsx("tbody", { style: styles.markdownTbody, ...props })),
        tr: ({ node, ...props }) => _jsx("tr", { style: styles.markdownTr, ...props }),
        th: ({ node, ...props }) => _jsx("th", { style: styles.markdownTh, ...props }),
        td: ({ node, ...props }) => _jsx("td", { style: styles.markdownTd, ...props }),
        ul: ({ node, ...props }) => _jsx("ul", { style: styles.markdownUl, ...props }),
        ol: ({ node, ...props }) => _jsx("ol", { style: styles.markdownOl, ...props }),
        li: ({ node, ...props }) => _jsx("li", { style: styles.markdownLi, ...props }),
        blockquote: ({ node, ...props }) => (_jsx("blockquote", { style: styles.markdownBlockquote, ...props })),
        a: ({ node, href, children, ...props }) => {
            const handleClick = (e) => {
                e.preventDefault();
                if (href) {
                    // Check if it's a tooltip link
                    if (href.startsWith("tooltip:")) {
                        // Do nothing on click for tooltips
                        return;
                    }
                    // Check if the URL matches any whitelisted URL or starts with one
                    const isWhitelisted = whitelistedURLs.some((whitelistedUrl) => href.startsWith(whitelistedUrl));
                    if (isWhitelisted) {
                        // Redirect in the same window
                        window.location.href = href;
                    }
                    else {
                        // Open in a new tab/window
                        window.open(href, "_blank", "noopener,noreferrer");
                    }
                }
            };
            if (href && href.startsWith("tooltip:")) {
                const tooltipText = decodeURIComponent(href.replace("tooltip:", ""));
                return (_jsx(Tooltip, { title: tooltipText, placement: "top", arrow: true, children: _jsx("span", { style: {
                            borderBottom: "1px dashed #0E497A",
                            cursor: "help",
                            color: "inherit",
                            zIndex: 9999,
                        }, ...props, children: children }) }));
            }
            return (_jsx("a", { href: href, onClick: handleClick, style: styles.markdownA, ...props, children: children }));
        },
        math: ({ children }) => {
            const value = String(children[0] || "");
            try {
                return (_jsx("span", { style: styles.mathBlockWrapper, children: _jsx("span", { dangerouslySetInnerHTML: {
                            __html: katex.renderToString(value.replace("\\(", "$$").replace("\\)", "$$"), {
                                displayMode: true,
                                throwOnError: false,
                            }),
                        }, style: styles.mathBlock }) }));
            }
            catch (error) {
                console.error("KaTeX error:", error);
                return _jsx("span", { style: styles.mathError, children: value });
            }
        },
        inlineMath: ({ children }) => {
            const value = String(children[0] || "");
            try {
                return (_jsx("span", { style: styles.inlineMathWrapper, children: _jsx("span", { dangerouslySetInnerHTML: {
                            __html: katex.renderToString(value.replace("\\(", "$$").replace("\\)", "$$"), {
                                displayMode: false,
                                throwOnError: false,
                            }),
                        }, style: styles.inlineMath }) }));
            }
            catch (error) {
                console.error("KaTeX error:", error);
                return _jsx("span", { style: styles.mathError, children: value });
            }
        },
    };
    // Add this function to handle tag changes
    const handleTagsChange = (tags) => {
        // You can add additional logic here if needed
    };
    // Update renderFilePreview to show progress bar below file info
    const renderFilePreview = () => {
        if (!fileData || !showFilePreview)
            return null;
        const isDocx = fileData.name.toLowerCase().endsWith(".docx");
        const isXlsx = fileData.name.toLowerCase().endsWith(".xlsx");
        const isPdf = fileData.name.toLowerCase().endsWith(".pdf");
        return (_jsxs("div", { style: CopilotStyles.prefilePreview, children: [isDocx ? (_jsx(FaFileWord, { style: CopilotStyles.filePreviewIcon })) : isXlsx ? (_jsx(FaFileExcel, { style: CopilotStyles.filePreviewIcon })) : isPdf ? (_jsx(FaFilePdf, { style: CopilotStyles.filePreviewIcon })) : (_jsx(FiPaperclip, { style: CopilotStyles.filePreviewIcon })), _jsxs("div", { style: CopilotStyles.filePreviewName, children: [fileData.name, " (", Math.round(fileData.size / 1024), " KB)"] }), isFileUploading && (_jsx("div", { style: {
                        width: "100%",
                        height: "4px",
                        background: "#e0e0e0",
                        borderRadius: "2px",
                        margin: "8px 0 0 0",
                        overflow: "hidden",
                    }, children: _jsx("div", { style: {
                            width: `${fileUploadProgress}%`,
                            height: "100%",
                            background: "#1976d2",
                            transition: "width 0.2s",
                        } }) })), _jsx("button", { onClick: () => {
                        setFileData(null);
                        setShowFilePreview(false);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                        }
                    }, style: CopilotStyles.filePreviewCloseButton, children: "\u2715" })] }));
    };
    // Add a helper function to render file attachments
    const renderFileAttachment = (fileInfo) => {
        const isDocx = fileInfo.name.toLowerCase().endsWith(".docx");
        const isXlsx = fileInfo.name.toLowerCase().endsWith(".xlsx");
        const isPdf = fileInfo.name.toLowerCase().endsWith(".pdf");
        const iconStyle = { color: "#0E497A", fontSize: "11px", flexShrink: 0 };
        return (_jsxs("div", { style: {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "#e7effe",
                padding: "4px 8px",
                borderRadius: "4px",
                marginBottom: "6px",
                alignSelf: "flex-start",
                fontSize: "12px",
            }, children: [isDocx ? (_jsx(FaFileWord, { style: iconStyle })) : isXlsx ? (_jsx(FaFileExcel, { style: iconStyle })) : isPdf ? (_jsx(FaFilePdf, { style: iconStyle })) : (_jsx(FiPaperclip, { style: iconStyle })), _jsx("span", { style: {
                        color: "#0E497A",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "150px",
                    }, children: fileInfo.name })] }));
    };
    useEffect(() => {
        console.log("File Data", fileData);
    }, [fileData]);
    // Update the renderMessage function to handle the new file-attached message format and display file attachments consistently.
    const renderMessage = (msg, index) => {
        if (msg.content.startsWith("form:")) {
            console.log("Form Received:", msg.content);
            const raw = msg.content.substring(5);
            let parsedForm;
            try {
                parsedForm = JSON.parse(raw);
            }
            catch (error) {
                console.error("Form parsing error:", error);
                return _jsx("div", { children: "Error parsing form data" });
            }
            const hideAdditionalContextField = /add questions/i.test(parsedForm.form_title);
            return (_jsx("div", { ref: (el) => registerMessageRef(index, el), style: { position: "relative" }, children: _jsx(Form, { form: {
                        form_id: parsedForm.form_id,
                        form_title: parsedForm.form_title,
                        form_fields: parsedForm.form_fields,
                        enableReviewMode: parsedForm.enableReviewMode,
                        submitButtonText: parsedForm.submitButtonText,
                        editButtonText: parsedForm.editButtonText,
                        confirmButtonText: parsedForm.confirmButtonText,
                        cancelButtonText: parsedForm.cancelButtonText,
                        cancelButtonPrompt: parsedForm.cancelButtonPrompt,
                    }, isExpanded: isExpanded, sessionId: sessionId || "", styles: {
                        form: styles.form,
                        formTitle: styles.formTitle,
                        cancelButton: styles.cancelButton,
                        submitButton: styles.submitButton,
                    }, summary: fileSummary, fileData: msg.fileData, hideAdditionalContextField: true, forceSubmitted: msg.formSubmitted, forceConfirmed: msg.formConfirmed, onFileRemove: () => {
                        setFileData(null);
                        setFileSummary(null);
                        setSummaryId(null); // Reset summary_id when file is removed
                        setMessages((prevMessages) => {
                            const newMessages = [...prevMessages];
                            const lastIndex = newMessages.length - 1;
                            if (lastIndex >= 0) {
                                newMessages[lastIndex] = {
                                    ...newMessages[lastIndex],
                                    fileData: null,
                                };
                            }
                            return newMessages;
                        });
                    }, onSubmit: (response) => {
                        // Mark form as submitted in message history
                        setMessages((prevMessages) => {
                            const newMessages = [...prevMessages];
                            if (newMessages[index]) {
                                newMessages[index] = { ...newMessages[index], formSubmitted: true, formConfirmed: true };
                            }
                            return newMessages;
                        });
                        if (parsedForm.form_title.includes("add questions")) {
                            setDocRemoveAfterAssistantMessages(null);
                        }
                        handleSendClick();
                        setShowFilePreview(false);
                        if (typeof response.form_data === "string") {
                            setTableData(JSON.parse(response.form_data));
                        }
                        getCopilotResponseStreaming({
                            query: JSON.stringify({
                                formResponse: response,
                                summary_id: summaryId,
                                accessToken: props.sessionVariables?.authorization,
                                org_id: props.sessionVariables?.Org_id,
                                author_id: props.sessionVariables?.Author_ID,
                            }),
                        });
                    }, onPdfUpload: async (file) => {
                        try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scora-flow/summarize-document`, {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${props.sessionVariables?.authorization || localStorage.getItem("accessToken")}`,
                                },
                                body: formData,
                            });
                            if (!response.ok) {
                                throw new Error(`Upload failed: ${response.status}`);
                            }
                            const data = await response.json();
                            console.log('Document summarized:', data);
                            // Store the unique key for later submission
                            setSummaryId(data.unique_key);
                            // Optional: Store summary for display
                            setFileSummary(data.final_summary);
                        }
                        catch (error) {
                            console.error('Error uploading PDF:', error);
                            // Handle error - maybe show a toast or error message
                        }
                    }, onCancel: () => {
                        // Mark form as submitted/cancelled in message history
                        setMessages((prevMessages) => {
                            const newMessages = [...prevMessages];
                            if (newMessages[index]) {
                                newMessages[index] = { ...newMessages[index], formSubmitted: true };
                            }
                            return newMessages;
                        });
                        if (parsedForm.cancelButtonPrompt) {
                            const prompt = parsedForm.cancelButtonPrompt;
                            // Check if it's a hidden message
                            const isHiddenMessage = prompt.startsWith("#") && prompt.endsWith("#");
                            if (!isHiddenMessage) {
                                // Send the prompt as a user message immediately
                                const userMessage = {
                                    content: prompt,
                                    role: "user",
                                };
                                setMessages((prevMessages) => [...prevMessages, userMessage]);
                            }
                            // Send the prompt to the backend
                            getCopilotResponseStreaming({
                                query: JSON.stringify({
                                    query: prompt,
                                    accessToken: props.sessionVariables?.authorization,
                                    org_id: props.sessionVariables?.Org_id,
                                    author_id: props.sessionVariables?.Author_ID,
                                }),
                            });
                        }
                        else {
                            setSummaryId(null); // Reset summary_id when form is cancelled
                            const lastUserIdx = messages
                                .map((m) => m.role)
                                .lastIndexOf("user");
                            if (lastUserIdx >= 0) {
                                setMessages([
                                    ...messages.slice(0, lastUserIdx + 1),
                                    { content: "Form cancelled", role: "assistant" },
                                ]);
                            }
                        }
                    } }) }));
        }
        else if (msg.content.startsWith("tags:")) {
            // Handle tags message
            // Don't modify messages here as it causes re-render issues
            // setMessages((prevMessages) => {
            //   const newMessages = [...prevMessages];
            //   newMessages[newMessages.length - 1] = {
            //     content: "Below are the generated tags. Please review and submit them.",
            //     role: "assistant" as const,
            //   };
            //   return newMessages;
            // })
            const tagsContent = msg.content.substring(5);
            console.log("tagsContent", tagsContent);
            let parsedTags;
            try {
                parsedTags = JSON.parse(tagsContent);
                return (_jsx(React.Fragment, { children: _jsx("div", { ref: (el) => registerMessageRef(index, el), children: _jsx("div", { style: {
                                marginTop: "10px",
                            }, children: _jsx(Tags, { tags: parsedTags, onTagsChange: handleTagsChange, showTags: true, fileData: {
                                    name: fileData?.name || "",
                                    size: fileData?.size || 0,
                                    data: fileData || null,
                                    summary: fileSummary || null,
                                }, summary: fileSummary, onFileRemove: () => {
                                    setFileData(null);
                                    setFileSummary(null);
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = "";
                                    }
                                }, onAction: async (parsedTags) => {
                                    setShowFilePreview(false);
                                    if (parsedTags.action === "submit") {
                                        getCopilotResponseStreaming({
                                            query: JSON.stringify({
                                                query: JSON.stringify({
                                                    ...JSON.parse(JSON.stringify(parsedTags.tags)),
                                                    additionalContext: parsedTags.additionalContext,
                                                    type: parsedTags.action,
                                                }),
                                                accessToken: props.sessionVariables?.authorization,
                                                org_id: props.sessionVariables?.Org_id,
                                                author_id: props.sessionVariables?.Author_ID,
                                            }),
                                        });
                                    }
                                    else if (parsedTags.action === "refresh") {
                                        setMessages((prevMessages) => {
                                            const newMessages = [...prevMessages];
                                            // Add a new message for the refresh status instead of replacing
                                            return [
                                                ...newMessages,
                                                {
                                                    content: parsedTags?.message?.content || "Unknown error",
                                                    role: parsedTags?.message?.role,
                                                },
                                            ];
                                        });
                                        getCopilotResponseStreaming({
                                            query: JSON.stringify({
                                                query: JSON.stringify({
                                                    ...JSON.parse(JSON.stringify(parsedTags.tags)),
                                                    additionalContext: parsedTags.additionalContext,
                                                    type: parsedTags.action,
                                                }),
                                                accessToken: props.sessionVariables?.authorization,
                                                org_id: props.sessionVariables?.Org_id,
                                                author_id: props.sessionVariables?.Author_ID,
                                            }),
                                        });
                                    }
                                } }) }) }) }, index));
            }
            catch (error) {
                console.error("Tags parsing error:", error);
                return (_jsxs("div", { children: ["Error parsing tags data:", error instanceof Error ? error.message : "Unknown error"] }));
            }
        }
        else if (msg.content.startsWith("blueprint:")) {
            // Handle blueprint message
            const blueprintContent = msg.content.substring(10); // Remove "blueprint:" prefix
            let parsedBlueprint;
            try {
                parsedBlueprint = JSON.parse(blueprintContent);
                return (_jsx(React.Fragment, { children: _jsx("div", { ref: (el) => registerMessageRef(index, el), children: _jsx("div", { style: { marginTop: "10px" }, children: _jsx(BlueprintGenerator, { tags: parsedBlueprint, isGenerating: !!activeLoader, isLocked: msg.blueprintSubmitted, onSubmit: (config) => {
                                    console.log("Blueprint Config:", config);
                                    // Update the message to mark blueprint as submitted/locked
                                    setMessages(prevMessages => prevMessages.map((m, i) => i === index ? { ...m, blueprintSubmitted: true } : m));
                                    // Send the blueprint configuration to the backend
                                    getCopilotResponseStreaming({
                                        query: JSON.stringify({
                                            blueprintConfig: config,
                                            accessToken: props.sessionVariables?.authorization,
                                            org_id: props.sessionVariables?.Org_id,
                                            author_id: props.sessionVariables?.Author_ID,
                                        }),
                                    });
                                } }) }) }) }, index));
            }
            catch (error) {
                console.error("Blueprint parsing error:", error);
                return (_jsxs("div", { children: ["Error parsing blueprint data:", error instanceof Error ? error.message : "Unknown error"] }));
            }
        }
        else if (msg.content.startsWith("buttons:")) {
            // Handle buttons message
            const buttonsContent = msg.content.substring("buttons:".length);
            const right = buttonsContent.includes("align");
            // Check if this button should be right-aligned
            const shouldAlignRight = (() => {
                try {
                    const buttonData = JSON.parse(buttonsContent);
                    return (Array.isArray(buttonData) &&
                        buttonData.some((button) => button.align === "right"));
                }
                catch {
                    return false;
                }
            })();
            // Check if the previous message is a form
            const previousMsg = messages[index - 1];
            const isPreviousMessageForm = previousMsg?.content.startsWith("form:");
            return (_jsx(React.Fragment, { children: _jsx("div", { ref: (el) => registerMessageRef(index, el), style: {
                        ...(!right ? { marginLeft: "50px" } : {}),
                        // If previous message is a form, move buttons up to align with form buttons
                        ...(isPreviousMessageForm ? {
                            marginTop: "-62px",
                            marginLeft: shouldAlignRight ? "auto" : "50px",
                            marginRight: shouldAlignRight ? "50px" : "0",
                            display: "flex",
                            justifyContent: shouldAlignRight ? "flex-end" : "flex-start",
                            position: "relative",
                            zIndex: 10,
                            pointerEvents: "none"
                        } : {}),
                    }, children: _jsx(Buttons, { buttons: buttonsContent, previousMessageWidth: lastMessageWidth, tableData: tableData, onButtonClick: (prompt, shouldTriggerUpload, buttonIndex, uploadLink, hasSuggestions, validationOptions, errorMessage) => {
                            // Check if button has an error message to display
                            if (errorMessage) {
                                showFileError(errorMessage);
                                return; // Don't proceed with other button actions if there's an error
                            }
                            // Mark this specific button as clicked
                            setClickedButtons((prev) => ({
                                ...prev,
                                [`${index}-${buttonIndex}`]: Date.now(), // Use message index + button index as unique identifier
                            }));
                            if (shouldTriggerUpload) {
                                // Store prompt and upload link for after file upload
                                setPendingButtonPrompt(prompt);
                                // Store validation options for file validation
                                setFileValidationOptions(validationOptions || null);
                                // Store upload link in a new state or pass it along
                                if (uploadLink) {
                                    // Store the custom upload link for use in getDocumentSummary
                                    localStorage.setItem("customUploadLink", uploadLink);
                                }
                                else {
                                    // Remove any existing custom upload link
                                    localStorage.removeItem("customUploadLink");
                                }
                                // Open file dialog
                                fileInputRef.current?.click();
                            }
                            else {
                                // Handle case where button has no prompt but has suggestions
                                if (!prompt && hasSuggestions && sessionId) {
                                    console.log("Button has no prompt but has suggestions enabled, fetching fresh suggestions directly");
                                    // Fetch fresh suggestions immediately since there's no query to send
                                    setTimeout(async () => {
                                        try {
                                            console.log("Fetching fresh suggestions for suggestions-only button...");
                                            const suggestions = await copilotEngine.getSuggestions(sessionId);
                                            if (suggestions && Array.isArray(suggestions)) {
                                                console.log("Fetched fresh suggestions from suggestions-only button:", suggestions);
                                                setSuggestions(suggestions);
                                            }
                                            else {
                                                console.log("No suggestions returned from getSuggestions call for suggestions-only button");
                                            }
                                        }
                                        catch (error) {
                                            console.error("Error fetching fresh suggestions from suggestions-only button:", error);
                                        }
                                    }, 100); // Short delay to ensure button state is updated
                                    return; // Exit early since there's no prompt to process
                                }
                                // Handle normal case with prompt
                                if (prompt) {
                                    // Handle reset chat action
                                    if (prompt === "action:reset_chat") {
                                        handleResetChat();
                                        return;
                                    }
                                    // Handle download file action
                                    if (prompt.startsWith("download_file:")) {
                                        const jobId = prompt.substring("download_file:".length);
                                        const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cron/jobs/${jobId}/download`;
                                        // Create a temporary link element for download
                                        const link = document.createElement("a");
                                        link.href = downloadUrl;
                                        link.download = `extracted_data_${jobId}.xlsx`; // Default filename
                                        // Add auth headers by fetching first, then creating blob URL
                                        try {
                                            const token = localStorage.getItem("accessToken");
                                            fetch(downloadUrl, {
                                                method: "GET",
                                                headers: {
                                                    Authorization: `Bearer ${token}`,
                                                },
                                            })
                                                .then((response) => {
                                                if (response.ok) {
                                                    return response.blob();
                                                }
                                                throw new Error("Failed to download file");
                                            })
                                                .then((blob) => {
                                                const blobUrl = URL.createObjectURL(blob);
                                                link.href = blobUrl;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                URL.revokeObjectURL(blobUrl);
                                            })
                                                .catch((error) => {
                                                console.error("Download failed:", error);
                                                // Show error message to user
                                                const errorMessage = {
                                                    content: "Failed to download file. Please try again.",
                                                    role: "assistant",
                                                };
                                                setMessages((prevMessages) => [
                                                    ...prevMessages,
                                                    errorMessage,
                                                ]);
                                            });
                                        }
                                        catch (error) {
                                            console.error("Download error:", error);
                                        }
                                        return; // Exit early for download action
                                    }
                                    // Handle direct download URL action
                                    if (prompt.startsWith("download_url:")) {
                                        const downloadPath = prompt.substring("download_url:".length);
                                        // Handle both absolute and relative URLs
                                        const downloadUrl = downloadPath.startsWith('http')
                                            ? downloadPath
                                            : `${process.env.REACT_APP_BACKEND_URL}${downloadPath}`;
                                        // Create a temporary link element for download
                                        const link = document.createElement("a");
                                        // Try to extract filename from URL or use default
                                        const filename = downloadPath.split('/').pop() || 'generated_file.xlsx';
                                        link.download = filename;
                                        // Add auth headers by fetching first, then creating blob URL
                                        try {
                                            const token = localStorage.getItem("accessToken");
                                            fetch(downloadUrl, {
                                                method: "GET",
                                                headers: {
                                                    Authorization: `Bearer ${token}`,
                                                },
                                            })
                                                .then((response) => {
                                                if (response.ok) {
                                                    return response.blob();
                                                }
                                                throw new Error("Failed to download file");
                                            })
                                                .then((blob) => {
                                                const blobUrl = URL.createObjectURL(blob);
                                                link.href = blobUrl;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                URL.revokeObjectURL(blobUrl);
                                            })
                                                .catch((error) => {
                                                console.error("Download failed:", error);
                                                // Show error message to user
                                                const errorMessage = {
                                                    content: "Failed to download file. Please try again.",
                                                    role: "assistant",
                                                };
                                                setMessages((prevMessages) => [
                                                    ...prevMessages,
                                                    errorMessage,
                                                ]);
                                            });
                                        }
                                        catch (error) {
                                            console.error("Download error:", error);
                                        }
                                        return; // Exit early for download action
                                    }
                                    // Check if it's a hidden message
                                    const isHiddenMessage = prompt.startsWith("#") && prompt.endsWith("#");
                                    if (!isHiddenMessage) {
                                        // Send the prompt as a user message immediately only if not hidden
                                        const userMessage = {
                                            content: prompt,
                                            role: "user",
                                        };
                                        setMessages((prevMessages) => [
                                            ...prevMessages,
                                            userMessage,
                                        ]);
                                    }
                                    // Get response from server (always send to server)
                                    getCopilotResponseStreaming({
                                        query: JSON.stringify({
                                            query: prompt,
                                            accessToken: props.sessionVariables?.authorization,
                                            org_id: props.sessionVariables?.Org_id,
                                            author_id: props.sessionVariables?.Author_ID,
                                            jobId: job_id
                                        }),
                                    });
                                    // If button has suggestions property set to true, fetch fresh suggestions after response
                                    if (hasSuggestions && sessionId) {
                                        console.log("Button has suggestions enabled, will fetch fresh suggestions after response");
                                        // Set a timeout to fetch fresh suggestions after the response is processed
                                        setTimeout(async () => {
                                            try {
                                                console.log("Fetching fresh suggestions for button with prompt...");
                                                const suggestions = await copilotEngine.getSuggestions(sessionId);
                                                if (suggestions && Array.isArray(suggestions)) {
                                                    console.log("Fetched fresh suggestions from button click:", suggestions);
                                                    setSuggestions(suggestions);
                                                }
                                                else {
                                                    console.log("No suggestions returned from getSuggestions call");
                                                }
                                            }
                                            catch (error) {
                                                console.error("Error fetching fresh suggestions from button click:", error);
                                            }
                                        }, 2500); // Wait 2.5 seconds for the response to be fully processed
                                    }
                                }
                                else if (!hasSuggestions) {
                                    // Button has no prompt and no suggestions - this might be an error case
                                    console.warn("Button clicked with no prompt and no suggestions enabled");
                                }
                            }
                        }, isExpanded: isExpanded, isLatestMessage: (() => {
                            // Find the latest button message index
                            // let latestButtonIndex = -1;
                            // for (let i = messages.length - 1; i >= 0; i--) {
                            //   if (messages[i].content.startsWith("buttons:")) {
                            //     latestButtonIndex = i;
                            //     break;
                            //   }
                            // }
                            // // This is the latest button message if this index matches the latest
                            // return latestButtonIndex === -1 || index === latestButtonIndex;
                            return messages.length - 1 === index;
                        })(), clickedButtonIndex: (() => {
                            // Find which button was clicked for this message
                            for (let buttonIdx = 0; buttonIdx < 10; buttonIdx++) {
                                // Check up to 10 buttons
                                if (clickedButtons[`${index}-${buttonIdx}`]) {
                                    return buttonIdx;
                                }
                            }
                            return null;
                        })(), sessionId: sessionId || undefined }) }) }, index));
        }
        else if (msg.content.startsWith("k_cards:")) {
            // Handle k_cards message
            const kCardsContent = msg.content.substring("k_cards:".length);
            let parsedFeatureCards;
            try {
                parsedFeatureCards = JSON.parse(kCardsContent);
            }
            catch (error) {
                console.error("Feature cards parsing error:", error);
                return null;
                // return <div key={index}>Error parsing feature cards data</div>;
            }
            return (_jsx(React.Fragment, { children: _jsx("div", { ref: (el) => registerMessageRef(index, el), style: {
                        ...{ marginLeft: "20px" },
                    }, children: _jsx(FeatureTemplates, { templates: parsedFeatureCards, selectedTemplateId: selectedFeatureTemplates[index] || null, onSelect: async (template) => {
                            setShowFilePreview(false);
                            // Track the selected template for this message
                            setSelectedFeatureTemplates((prev) => ({
                                ...prev,
                                [index]: template.id,
                            }));
                            // Set chat disabled state based on template property
                            setIsChatDisabled(template.isChatDisabled === "true");
                            console.log("Sending Out Template Intent:", template);
                            getCopilotResponseStreaming({
                                query: JSON.stringify({
                                    query: template.query,
                                    accessToken: props.sessionVariables?.authorization,
                                    org_id: props.sessionVariables?.Org_id,
                                    author_id: props.sessionVariables?.Author_ID,
                                }),
                            });
                            setHasUserInteracted(true);
                        }, isReadonly: false, styles: {
                            featureTemplatesContainer: styles.featureTemplatesContainer,
                            featureTemplateCard: styles.featureTemplateCard,
                            featureTemplateIcon: styles.featureTemplateIcon,
                            featureTemplateContent: styles.featureTemplateContent,
                            featureTemplateTitle: styles.featureTemplateTitle,
                            featureTemplateDescription: styles.featureTemplateDescription,
                        } }) }) }, index));
        }
        else if (msg.content.startsWith("loader:")) {
            const loaderContent = msg.content.substring(7);
            let parsedLoader;
            try {
                parsedLoader = JSON.parse(loaderContent);
                // If this is the active loader, show current state from state, otherwise parsed content
                const loaderToRender = (activeLoader && activeLoader.id === parsedLoader.id)
                    ? activeLoader
                    : parsedLoader;
                return (_jsx("div", { ref: (el) => registerMessageRef(index, el), style: { marginLeft: "50px" }, children: _jsx(ProgressLoader, { loader: loaderToRender }) }, index));
            }
            catch (e) {
                return null;
            }
        }
        else if (msg.content.startsWith("uploader:")) {
            // Handle uploader message
            const uploaderContent = msg.content.substring("uploader:".length);
            let parsedUploaderConfig;
            try {
                parsedUploaderConfig = JSON.parse(uploaderContent);
            }
            catch (error) {
                console.error("Uploader config parsing error:", error);
                return _jsx("div", { children: "Error parsing uploader configuration" }, index);
            }
            return (_jsx(React.Fragment, { children: _jsx("div", { ref: (el) => registerMessageRef(index, el), style: {
                        marginLeft: "20px",
                    }, children: _jsx(Uploader, { config: {
                            ...parsedUploaderConfig,
                            // Add authorization headers for API calls
                            apiCallConfig: parsedUploaderConfig.apiCallConfig
                                ? {
                                    ...parsedUploaderConfig.apiCallConfig,
                                    headers: {
                                        Authorization: `Bearer ${props.sessionVariables?.authorization ||
                                            localStorage.getItem("accessToken")}`,
                                        ...parsedUploaderConfig.apiCallConfig.headers,
                                    },
                                }
                                : undefined,
                        }, onSubmit: async (files) => {
                            // Optional callback - don't add messages during upload/polling phase
                            console.log("Files submitted from uploader:", files);
                        }, onJobComplete: async (success, jobId, message) => {
                            // Server now handles chat responses, so we just need to remove the uploader
                            setMessages((prevMessages) => prevMessages.filter((msg) => (!msg.content.startsWith("uploader:") &&
                                !msg.content.includes("Upload your files to create question items") &&
                                !msg.content.includes("upload your files") &&
                                !msg.content.toLowerCase().includes("upload")) ||
                                msg.role === "user" // Keep user messages even if they mention upload
                            ));
                        }, onRemoveFromChat: () => {
                            // Remove uploader-related messages from chat
                            setMessages((prevMessages) => prevMessages.filter((msg) => (!msg.content.startsWith("uploader:") &&
                                !msg.content.includes("Upload your files to create question items") &&
                                !msg.content.includes("upload your files") &&
                                !msg.content.toLowerCase().includes("upload")) ||
                                msg.role === "user" // Keep user messages even if they mention upload
                            ));
                        }, setJobId: setJobId, styles: {}, isExpanded: isExpanded, sessionId: sessionId || undefined, getCopilotResponseStreaming: getCopilotResponseStreaming }) }) }, index));
        }
        else if (msg.role === "user" && !msg.content.startsWith("<start>")) {
            return (_jsx("div", { style: styles.userMessageContainer, ref: (el) => registerMessageRef(index, el), children: _jsx("div", { style: {
                        ...styles.message,
                        ...styles.userMessage,
                    }, children: _jsx(ReactMarkdown, { remarkPlugins: [remarkMath, remarkGfm], rehypePlugins: [rehypeKatex], components: components, children: msg.content }) }) }, index));
        }
        else {
            // Only render messages that are not queries
            return (msg.role === "load" && index === messages.length - 1) ||
                (msg.role === "user" && !msg.content.startsWith("<start>")) ||
                msg.role === "assistant" ? (_jsxs("div", { ref: (el) => registerMessageRef(index, el), style: {
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    justifyContent: "left",
                    alignItems: "center",
                    width: "fit-content",
                }, children: [_jsxs("div", { style: msg.role === "user"
                            ? styles.userMessageContainer
                            : styles.messageContainer, children: [(msg.role === "assistant" || msg.role === "load") && (_jsx("div", { style: styles.messageAvatar, children: _jsx("img", { src: avatarPath, alt: "Calyb Avatar", style: styles.messageAvatarImg }) })), _jsx("div", { children: _jsx("div", { ref: msg.role === "assistant" && index === messages.length - 2
                                        ? lastMessageRef
                                        : null, className: msg.role === "load" ? "loading-message" : "", style: {
                                        ...styles.message,
                                        ...(msg.role === "user"
                                            ? styles.userMessage
                                            : msg.role === "load"
                                                ? {
                                                    ...styles.loadingMessage,
                                                    animation: "bounce 1.5s ease-in-out infinite",
                                                }
                                                : styles.botMessage),
                                        whiteSpace: "normal",
                                        display: "block",
                                    }, children: _jsx(ReactMarkdown, { remarkPlugins: [remarkMath, remarkGfm], rehypePlugins: [rehypeKatex], components: components, children: msg.content === "file_upload_loading"
                                            ? "Uploading..."
                                            : msg.content }) }) })] }, index), msg.content === "file_upload_loading" ? (_jsx("div", { style: { alignSelf: "center" }, children: _jsx(FaSpinner, { color: "#FFBE00", size: 24 }) })) : null, msg.content === "**Done!**" ? (_jsxs("div", { style: {
                            alignSelf: "center",
                            fontFamily: "IBM Plex Sans, sans-serif",
                        }, children: [String(msg.fileData?.name).includes("docx") ? (_jsx(FaFileWord, { color: "blue", size: 24 })) : String(msg.fileData?.name).includes("xlsx") ? (_jsx(FaFileExcel, { color: "green", size: 24 })) : (_jsx(FaFilePdf, { color: "red", size: 24 })), " ", "\u00A0 ", msg.fileData?.name] })) : null] })) : null;
        }
    };
    // Add this helper function inside CalybCopilot
    const handleAssistantMessage = (newMsg) => {
        const isFormMsg = (msg) => typeof msg.content === "string" && msg.content.startsWith("form:");
        const isTagsMsg = (msg) => typeof msg.content === "string" && msg.content.startsWith("tags:");
        const isTokenMsg = (msg) => typeof msg.content === "string" && msg.content.startsWith("token:");
        const isUploaderMsg = (msg) => typeof msg.content === "string" && msg.content.startsWith("uploader:");
        if (isTagsMsg(newMsg)) {
            setDocRemoveAfterAssistantMessages(2);
            return;
        }
        if (isFormMsg(newMsg)) {
            setDocRemoveAfterAssistantMessages(2);
            return;
        }
        if (isUploaderMsg(newMsg)) {
            setDocRemoveAfterAssistantMessages(2);
            return;
        }
        if (isTokenMsg(newMsg)) {
            setDocRemoveAfterAssistantMessages(null);
            return;
        }
        // Edge case: If a normal assistant message is shown right after forms or tags, remove doc immediately
        if (docRemoveAfterAssistantMessages !== null) {
            setFileData(null);
            setShowFilePreview(false);
            setFileSummary(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setDocRemoveAfterAssistantMessages(null);
            setMessages((prevMessages) => {
                const newMessages = [...prevMessages];
                const nextIdx = newMessages.findIndex((msg, idx) => idx > 0 && newMessages[idx - 1].fileData != null);
                if (nextIdx !== -1 && newMessages[nextIdx].fileData) {
                    newMessages[nextIdx] = { ...newMessages[nextIdx], fileData: null };
                }
                return newMessages;
            });
            return;
        }
        // If not form/tags and no counter, remove doc immediately
        setFileData(null);
        setShowFilePreview(false);
        setFileSummary(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setMessages((prevMessages) => {
            const newMessages = [...prevMessages];
            const nextIdx = newMessages.findIndex((msg, idx) => idx > 0 && newMessages[idx - 1].fileData != null);
            if (nextIdx !== -1 && newMessages[nextIdx].fileData) {
                newMessages[nextIdx] = { ...newMessages[nextIdx], fileData: null };
            }
            return newMessages;
        });
    };
    // Job Polling Effect
    useEffect(() => {
        let pollInterval;
        if (activeLoader) {
            const poll = async () => {
                try {
                    // Use the poll_url from the loader, but ensure it's prefixed with backend URL if needed
                    // Assuming poll_url is partial like /api/job/...
                    const url = `${process.env.REACT_APP_BACKEND_URL}${activeLoader.poll_url}`;
                    const response = await fetch(url, {
                        headers: {
                            'Authorization': `Bearer ${props.sessionVariables?.authorization || localStorage.getItem('accessToken')}`
                        }
                    });
                    if (!response.ok)
                        throw new Error('Polling failed');
                    const data = await response.json();
                    console.log("Poll result:", data);
                    if (data.status === 'completed') {
                        setActiveLoader(null);
                        // Remove the loader message from chat
                        setMessages((prevMessages) => prevMessages.filter(msg => {
                            if (!msg.content.startsWith("loader:"))
                                return true;
                            try {
                                const loaderData = JSON.parse(msg.content.substring(7));
                                return loaderData.id !== activeLoader.id;
                            }
                            catch {
                                return true;
                            }
                        }));
                        // If there's a result, add it as a new assistant message
                        if (data.result) {
                            // Check if result is a direct file download
                            if (data.result.download_url) {
                                // Create a button message for download instead of text link
                                const buttons = JSON.stringify([
                                    {
                                        text: "Back to Home",
                                        prompt: "action:reset_chat",
                                        color: "blue",
                                        align: "right"
                                    },
                                    {
                                        text: "Download Generated File",
                                        prompt: `download_url:${data.result.download_url}`,
                                        color: "green",
                                        align: "right"
                                    },
                                ]);
                                setMessages(prev => [
                                    ...prev,
                                    {
                                        role: 'assistant',
                                        content: "Your question items has been generated successfully! You can download it below.",
                                    },
                                    {
                                        role: 'assistant',
                                        content: `buttons:${buttons}`
                                    }
                                ]);
                            }
                            else if (data.result.questions) {
                                // If it returns questions/content directly (unlikely for async but possible)
                                setMessages(prev => [...prev, {
                                        role: 'assistant',
                                        content: "Generation complete due to result." // Placeholder
                                    }]);
                            }
                        }
                    }
                    else if (data.status === 'failed') {
                        setActiveLoader(null);
                        showFileError(`Generation failed: ${data.message || 'Unknown error'}`);
                    }
                    else {
                        // Update active loader with all new data (progress, status, counts, etc.)
                        setActiveLoader(prev => prev ? ({
                            ...prev,
                            text: data.message || prev.text,
                            progress: data.progress,
                            current_item: data.current_item,
                            items_processed: data.items_processed,
                            total_items: data.total_items,
                            generated_count: data.generated_count,
                            status: data.status
                        }) : null);
                    }
                }
                catch (error) {
                    console.error("Polling error:", error);
                    // Don't kill the loader on single network error, but maybe after threshold?
                }
            };
            // Poll immediately
            poll();
            // Then poll every 2 seconds
            pollInterval = setInterval(poll, 2000);
        }
        return () => {
            if (pollInterval)
                clearInterval(pollInterval);
        };
    }, [activeLoader?.id]);
    // Progress Loader Component
    const ProgressLoader = ({ loader }) => {
        // Determine progress percentage
        const progress = loader.progress !== undefined ? loader.progress : 0;
        return (_jsxs("div", { style: {
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
                padding: "20px",
                borderRadius: "16px",
                margin: "12px 0",
                gap: "16px",
                border: "1px solid #e2e8f0",
                width: "100%",
                maxWidth: "600px",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
            }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: "12px" }, children: [_jsx("div", { style: {
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }, children: _jsx(FaSpinner, { className: "spin", color: "#0E497A", size: 20, style: { animation: "spin 1s linear infinite" } }) }), _jsx("div", { style: {
                                fontSize: "14px",
                                color: "#0E497A",
                                fontFamily: '"IBM Plex Sans", sans-serif',
                                fontWeight: 600,
                                flex: 1
                            }, children: loader.status === 'processing' ? 'Generating Questions...' : 'Initializing...' }), loader.progress !== undefined && (_jsxs("div", { style: { fontSize: "12px", color: "#6c757d", fontWeight: 600, fontFamily: '"IBM Plex Sans", sans-serif' }, children: [loader.progress, "%"] }))] }), _jsx("div", { style: { width: "100%", height: "6px", backgroundColor: "#e9ecef", borderRadius: "3px", overflow: "hidden" }, children: _jsx("div", { style: {
                            width: `${Math.max(5, progress)}%`,
                            height: "100%",
                            backgroundColor: "#0E497A",
                            borderRadius: "3px",
                            transition: "width 0.5s ease-in-out"
                        } }) }), _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "4px" }, children: [_jsx("div", { style: {
                                fontSize: "13px",
                                color: "#495057",
                                fontFamily: '"IBM Plex Sans", sans-serif',
                                lineHeight: "1.4"
                            }, children: loader.text || loader.message }), loader.items_processed !== undefined && loader.total_items !== undefined && (_jsxs("div", { style: { fontSize: "11px", color: "#6c757d", marginTop: "4px", fontFamily: '"IBM Plex Sans", sans-serif' }, children: ["Processing: ", loader.current_item || 'Unknown', " (", loader.items_processed, "/", loader.total_items, ")"] })), loader.generated_count !== undefined && (_jsxs("div", { style: { fontSize: "11px", color: "#28a745", fontWeight: 600, fontFamily: '"IBM Plex Sans", sans-serif' }, children: [loader.generated_count, " questions generated so far"] }))] })] }));
    };
    // File Upload Spinner Component
    const FileUploadSpinner = () => (_jsxs("div", { style: {
            display: "flex",
            alignItems: "center",
            backgroundColor: "#f8f9fa",
            padding: "12px 16px",
            borderRadius: "12px",
            margin: "8px 0",
            gap: "12px",
            border: "1px solid #e9ecef",
        }, children: [_jsx("div", { style: {
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#fff3cd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                }, children: _jsx("div", { style: {
                        width: "24px",
                        height: "24px",
                        border: "3px solid #FFBE00",
                        borderTop: "3px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                    } }) }), _jsx("div", { style: {
                    flex: 1,
                    fontSize: "14px",
                    color: "#495057",
                    fontFamily: '"IBM Plex Sans", sans-serif',
                }, children: "Uploading... please hang tight!\"" }), _jsxs("div", { style: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "2px",
                }, children: [_jsx("div", { style: {
                            width: "6px",
                            height: "6px",
                            backgroundColor: "#FFBE00",
                            borderRadius: "50%",
                            animation: "jump 0.6s ease-in-out infinite",
                        } }), _jsx("div", { style: {
                            width: "6px",
                            height: "6px",
                            backgroundColor: "#FFBE00",
                            borderRadius: "50%",
                            animation: "jump 0.6s ease-in-out 0.1s infinite",
                        } }), _jsx("div", { style: {
                            width: "6px",
                            height: "6px",
                            backgroundColor: "#FFBE00",
                            borderRadius: "50%",
                            animation: "jump 0.6s ease-in-out 0.2s infinite",
                        } })] })] }));
    // Component rendering
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: fontImportStyle }), _jsx("div", { draggable: !isOpen, onDragStart: onDragStart, onDragEnd: onDragEnd, onDragOver: onDragOver, style: {
                    ...styles.toggleDiv,
                    ...(isOpen ? styles.toggleDivOpen : {}),
                    ...(isExpanded ? styles.toggleDivExpanded : {}),
                    ...(togglePosition.x !== 0 || togglePosition.y !== 0
                        ? {
                            position: "fixed",
                            left: `${togglePosition.x}px`,
                            top: `${togglePosition.y}px`,
                            right: "auto",
                            bottom: "auto",
                        }
                        : {}),
                    ...(isDragging
                        ? {
                            opacity: 0.7,
                            cursor: "grabbing",
                            zIndex: 10000,
                        }
                        : {
                            cursor: isOpen ? "pointer" : "grab",
                        }),
                }, children: _jsx("button", { onClick: (e) => {
                        // Prevent click when dragging or just finished dragging
                        if (!isDragging && !justDragged) {
                            toggleCopilot();
                        }
                    }, style: styles.copilotToggle, "aria-label": "Toggle copilot", children: _jsx("img", { src: logoPath, alt: "copilot Logo", style: styles.copilotToggleImg }) }) }), isOpen && (_jsxs("div", { style: {
                    ...styles.chatContainer,
                    ...(isExpanded ? styles.chatContainerExpanded : {}),
                }, children: [isResetting && (_jsx("div", { style: styles.loadingOverlayStyles, children: _jsx("div", { style: styles.spinnerStyles }) })), _jsxs("div", { style: styles.header, children: [_jsx("img", { src: headerLogoPath, alt: "header Logo", style: {
                                    ...styles.headerLogo,
                                    ...(isExpanded ? styles.headerLogoExpanded : {}),
                                    transition: "all 0.3s ease",
                                } }), _jsx("span", { style: styles.headerTitle, children: headerTitle }), _jsxs("div", { style: styles.headerButtons, children: [_jsx("button", { style: {
                                            ...styles.headerButton,
                                            ...(isExpanded ? styles.headerButtonHover : {}),
                                        }, onClick: handleExpandClick, "aria-label": "Expand chat", children: _jsx("img", { src: isExpanded
                                                ? `${process.env.REACT_APP_ASSETS_URL}/assets/FullScreenSelected.png`
                                                : `${process.env.REACT_APP_ASSETS_URL}/assets/FullScreenIcon.png`, alt: "Expand chat", style: styles.headerIcon }) }), _jsx("button", { style: {
                                            ...styles.headerButton,
                                            ...(isResetting ? styles.headerButtonHover : {}),
                                        }, onClick: handleResetChat, "aria-label": "Reset chat", children: props.headerIcons?.reset ? (_jsx("img", { src: props.headerIcons.reset, alt: "Reset chat", style: styles.headerIcon })) : (_jsx("img", { src: `${process.env.REACT_APP_ASSETS_URL}/assets/ReloadIcon.png`, alt: "Reset chat", style: styles.headerIcon })) }), _jsx("button", { style: {
                                            ...styles.headerButton,
                                            ...(isExpanded ? styles.headerButtonHover : {}),
                                        }, onClick: handleCloseChat, "aria-label": "Close chat", children: props.headerIcons?.close ? (_jsx("img", { src: props.headerIcons.close, alt: "Close chat", style: styles.headerIcon })) : (_jsx("img", { src: `${process.env.REACT_APP_ASSETS_URL}/assets/CloseIcon.png`, alt: "Close chat", style: styles.headerIcon })) })] })] }), _jsxs("div", { style: styles.messages, className: "messages-container", ref: messagesContainerRef, children: [_jsx("style", { children: `
                @keyframes fadeInFromTop {
                0% {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                    100% {
                  opacity: 1;
                  transform: translateY(0);
                }
              }

                @keyframes jump {
                0% { transform: translateY(0); }
                    50% { transform: translateY(-5px); } 
                    100% { transform: translateY(0); }
              }

                @keyframes spin {
                0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
              }

                @keyframes shine {
                0% {
                  background- position: - 200 %;
          }
          100 % {
            background- position: 200 %;
        }
                }

      @keyframes bounce {
        0 %, 100 % {
          transform: translateY(0);
        }
        50 % {
          transform: translateY(-4px);
        }
      }

                .loading - message {
        animation: bounce 1.5s ease -in -out infinite;
      }
      ` }), messages.map((msg, index) => {
                                return renderMessage(msg, index);
                            }), suggestions.length > 0 &&
                                !isChatDisabled &&
                                messages.length > 0 &&
                                messages[messages.length - 1].role === "assistant" &&
                                messages[messages.length - 1].role !== "load" && (_jsx("div", { style: {
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                    padding: "16px 20px",
                                    marginLeft: "60px",
                                    marginBottom: "8px",
                                }, children: suggestions.map((suggestion, index) => (_jsx("button", { className: "suggestion-button", style: {
                                        backgroundColor: "#0E497A",
                                        border: "1px solid #0E497A",
                                        borderRadius: "20px",
                                        padding: "8px 16px",
                                        fontSize: "14px",
                                        color: "white",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        fontFamily: '"IBM Plex Sans", sans-serif',
                                        flex: "0 0 auto",
                                        whiteSpace: "nowrap",
                                    }, onClick: () => handleSuggestionClick(suggestion), children: suggestion }, index))) })), _jsx("div", { ref: messagesEndRef }), showStartingUserMessages && (_jsxs("div", { style: styles.startingUserMessages, children: ["Get Started", startingUserMessages.map((message, index) => (_jsx("button", { style: styles.startingUserMessageButton, onClick: () => {
                                            handleSuggestionClick(message);
                                            setShowStartingUserMessages(false);
                                        }, children: _jsx("div", { style: styles.startingUserMessage, children: message }, index) }, index)))] })), isWalkthroughReady && (_jsxs("div", { style: styles.walkthroughButtons, children: [_jsxs("button", { style: { ...styles.walkthroughButton, ...styles.startButton }, "aria-label": "Start Walkthrough", onClick: handleStartWalkthrough, children: [_jsx(FaPlayCircle, { size: 16 }), "Start walkthrough"] }), _jsx("button", { style: {
                                            ...styles.walkthroughButton,
                                            ...styles.nevermindButton,
                                        }, "aria-label": "Nevermind", onClick: () => {
                                            setIsWalkthroughReady(false);
                                        }, children: "Nevermind" })] }))] }), _jsxs("div", { style: {
                            ...styles.inputArea,
                            padding: "10px 20px",
                            minHeight: "40px",
                        }, children: [_jsx("input", { type: "file", ref: fileInputRef, onChange: handleFileChange, accept: ".pdf,application/pdf,.xlsx,.docx", style: { display: "none" }, id: "file-upload-input" }), _jsxs("div", { style: {
                                    position: "relative",
                                    width: "100%",
                                    ...(fileData ? { marginTop: "8px" } : {}),
                                }, children: [showFilePreview && renderFilePreview(), _jsx("input", { className: "calyb-copilot-chat-input", ref: textareaRef, type: "text", style: {
                                            ...styles.chatInput,
                                            minHeight: "36px",
                                            padding: "8px 12px",
                                            ...(document.activeElement === textareaRef.current
                                                ? styles.chatInputFocus
                                                : {}),
                                            ...(isChatDisabled
                                                ? { opacity: 0.5, cursor: "not-allowed" }
                                                : {}),
                                        }, value: input, onChange: handleInputChange, onKeyDown: (e) => {
                                            if (e.key === "Enter" &&
                                                !e.shiftKey &&
                                                !sendDisabledMessage() &&
                                                !isChatDisabled) {
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }, placeholder: "Ask anything...", disabled: isChatDisabled })] }), _jsxs("div", { style: { display: "flex", gap: "8px" }, children: [_jsx("button", { style: {
                                            ...styles.sendButton,
                                            minWidth: "36px",
                                            height: "36px",
                                            padding: "8px",
                                            ...(sendDisabledMessage() || isChatDisabled
                                                ? styles.sendButtonDisabled
                                                : {}),
                                            ...(!sendDisabledMessage() && !isChatDisabled && isHovered
                                                ? styles.sendButtonHover
                                                : {}),
                                        }, onClick: handleSendClick, disabled: sendDisabledMessage() || isChatDisabled, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), children: _jsx("img", { src: `${process.env.REACT_APP_ASSETS_URL}/assets/SendButton.png`, alt: "Send", style: {
                                                // ...styles.sendIcon,
                                                height: "40px",
                                                width: "40px",
                                                ...(sendDisabledMessage() ? styles.sendIconDisabled : {}),
                                            } }) }), _jsx("div", { onClick: () => !isChatDisabled && fileInputRef.current?.click(), style: {
                                            height: "36px",
                                            width: "36px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: isChatDisabled ? "not-allowed" : "pointer",
                                            opacity: isChatDisabled ? 0.5 : 1,
                                        }, onMouseEnter: () => !isChatDisabled && setIsFileButtonHovered(true), onMouseLeave: () => !isChatDisabled && setIsFileButtonHovered(false), children: _jsx("img", { src: `${process.env.REACT_APP_ASSETS_URL}/assets/UploadButton.png`, alt: "File", color: fileData ? "#0E497A" : "#000000", style: {
                                                height: "40px",
                                                width: "40px",
                                            } }) })] }), fileError && (_jsx("div", { style: {
                                    position: "absolute",
                                    bottom: "70px",
                                    left: "20px",
                                    backgroundColor: "#f44336",
                                    color: "white",
                                    padding: "12px 16px",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    fontFamily: '"IBM Plex Sans", System-UI, sans-serif',
                                    fontWeight: "400",
                                    lineHeight: "1.4",
                                    zIndex: 1000,
                                    maxWidth: "80%",
                                    minWidth: "200px",
                                    boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                                    border: "1px solid #d32f2f",
                                }, children: fileError }))] }), _jsx("div", { style: styles.footer, children: "Need fast facts on Assessments or learn more about SCORA\u00AE? Get rapid-fire answers here from Dia." })] })), isWalkthroughActive && (_jsx(Tour, { steps: tourSteps, onFinish: handleFinishWalkthrough })), _jsx("style", { children: `
        .calyb - copilot - chat - input::placeholder {
            ${styles.chatInputPlaceholder
                    ? convertStylesToCSS(styles.chatInputPlaceholder)
                    : ""}
      }
          .suggestion - button:hover {
        background - color: #e0e0e0;
      }

          /* Custom scrollbar for messages container */
          .messages - container:: -webkit - scrollbar {
        width: 14px;
      }
          
          .messages - container:: -webkit - scrollbar - track {
        background: #f1f8fe;
        border - radius: 0;
      }
          
          .messages - container:: -webkit - scrollbar - thumb {
        background: #d5dde4;
        border - radius: 0;
      }
          
          .messages - container:: -webkit - scrollbar - thumb:hover {
        background: #c5d0d8;
      }

          /* Firefox scrollbar */
          .messages - container {
        scrollbar - width: thin;
        scrollbar - color: #d5dde4 #f1f8fe;
      }

          /* Suggestion button hover and active states */
          .suggestion - button:hover {
        background - color: #0A3B5C!important;
        transform: translateY(-1px);
        box - shadow: 0 4px 8px rgba(14, 73, 122, 0.3);
      }

          .suggestion - button:active {
        background - color: #FFBE00!important;
        color: #0E497A!important;
        transform: translateY(0);
        box - shadow: 0 2px 4px rgba(255, 190, 0, 0.3);
      }
      ` })] }));
}
export default CalybCopilot;
